import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import findLast from 'lodash/findLast';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';
import { IEmployeeLaborJobState, ILaborDataPayee, ITipsBreakdown, ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import { ICurrentEmployee } from 'store/types';
import {
  CalculatorParticipantType,
  CashPayoutDetails,
  ChangeLogAction,
  ChangeLogEntity,
  ChangeLogParticipantType,
  CheckModifiedDetails,
  Days,
  DEFAULT_POOL_ID,
  DEFAULT_POOL_NAME,
  DEFAULT_POOL_UNKNOWN_JOB_TITLE,
  DisbursementMethod,
  EmployeeType,
  HoldbackEditedDetails,
  IApiError,
  ICalculatorParticipant,
  ICalculatorRule,
  ICalculatorRules,
  ICalculatorRulesSource,
  ICategoryGroup,
  ICheck,
  ICreatePaymentPayload,
  IDaySegment,
  IEmployee,
  IEmployeePayrollTip,
  IHoldbackFixed,
  IHoldbackPercent,
  IPayeeEmployee,
  IPaymentAdjustment,
  IPaymentSet,
  IPaymentSetCalculations,
  IPaymentSetCalculationsSettings,
  IPaymentSetDaySegment,
  IPaymentTotals,
  IPaymentWithholdings,
  IPoolData,
  IPoolEmployeeRow,
  IPoolJob,
  IPoolReceivedFrom,
  IPoolReport,
  IPOSData,
  IPOSProviderSchedule,
  IPOSResults,
  IPOSSegment,
  IPOSSegmentTotals,
  IPOSTicket,
  IPOSTicketCategory,
  IPOSTicketSplitOverrideShare,
  IPOSTicketTotals,
  IPosUnclosedChecksError,
  ISite,
  ISiteRolePolicy,
  ISplitParticipant,
  ITipCalculatorChangeLogEntry,
  ITipCalculatorEmployeeTicket,
  ITipCalculatorErrorPayload,
  ITipCalculatorFriendlyErrorItem,
  ITipCalculatorParticipant,
  ITipCalculatorPayee,
  ITipCalculatorPayeeError,
  ITipCalculatorRequest,
  ITipCalculatorRequestEmployee,
  ITipCalculatorRequestEmployeeSchedule,
  ITipCalculatorResult,
  ITipCalculatorResultEmployee,
  ITipCalculatorResultIgnoredCheck,
  ITipCalculatorResultParticipant,
  ITipCalculatorResultReceived,
  ITipCalculatorResultSplitChecks,
  LaborDataDetails,
  PaymentWorkflowTypes,
  PayoutRefType,
  RuleFundSource,
  ShareRuleType,
  SiteRole,
  SourcePaymentType,
  SourceType,
  TicketOverrideType,
  TipCalculatorMethod,
  TipCalculatorPayeeErrorType,
  TipCalculatorReceivedDistributionType,
  TipCalculatorWarningType,
  TipEditedDetails,
} from 'types';
import {
  buildPaymentPayouts,
  calculateHoldbackValue,
  calculateInstant,
  calculatePayeeTotals,
  calculatePayroll,
  centsToDollarsDisplay,
  Duration,
  getRolePolicy,
  normalizeJobTitle,
  siteUsesWithholdings,
  sortAlphaNumerically,
  sumBy,
  toPercent,
  toTitleCase,
} from 'utils';

dayjs.extend(isSameOrBefore);

interface IPOSResultSegment {
  id: string;
  name: string;
  start?: string;
  end?: string;
  endsNextDay?: boolean;
}

export class TipCalculationNoDataError extends Error {
  constructor(message = 'No data.') {
    super(message);
    Object.setPrototypeOf(this, TipCalculationNoDataError.prototype);
    this.name = 'TipCalculationNoDataError';
  }
}

export enum TipCalculationValidationType {
  NO_DATA = 'no_data',
  NO_RULE_FOR_JOB = 'no_rule_for_job',
  NO_TIP_SHARE_RULE_FOR_JOB = 'no_tip_share_rule_for_job',
  ZERO_POINT_RULE_FOR_JOB = 'zero_point_rule_for_job',
  EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES = 'employees_invalid_time_clock_entries',
  EMPLOYEES_STILL_CLOCKED_IN = 'employees_still_clocked_in',
  EMPLOYEES_NO_BREAK_END = 'employees_no_break_end',
  NO_ELIGIBLE_EMPLOYEES = 'no_eligible_employees',
  NO_LABOR_FOR_TICKETS = 'no_labor_for_tickets',
  NO_TIPS_OR_AUTO_GRATUITIES_TO_DISTRIBUTE = 'no_tips_or_auto_gratuities_to_distribute',
}

export interface ITipCalcValidationError {
  type: TipCalculationValidationType;
  title: string;
  message: string;
  jobs?: string[] | { title: string; total: number; employees: { name: string; total: number }[] }[];
  employees?: { name: string; jobTitle: string; start: string; end: string; totalMinWorked: number }[];
}

export class PosUnclosedChecksError extends Error {
  public errors: IPosUnclosedChecksError[];

  constructor(errors: IPosUnclosedChecksError[]) {
    super('Unclosed checks.');
    Object.setPrototypeOf(this, PosUnclosedChecksError.prototype);
    this.name = 'PosUnclosedChecksError';
    this.errors = errors;
  }
}

export class TipCalculationValidationError extends Error {
  public errors: ITipCalcValidationError[];

  constructor(message: string, errors: ITipCalcValidationError[]) {
    super(message);
    Object.setPrototypeOf(this, TipCalculationValidationError.prototype);
    this.name = 'TipCalculationValidationError';
    this.errors = errors;
  }
}

export interface INoLaborForTicketsError {
  code?: string;
  msg: string;
  allowSkip?: boolean;
  tickets?: ITipCalculatorEmployeeTicket[];
}

export class TipCalculationNoLaborForTicketsError extends Error {
  public errors: INoLaborForTicketsError[];

  constructor(message: string, errors: INoLaborForTicketsError[]) {
    super(message);
    Object.setPrototypeOf(this, TipCalculationNoLaborForTicketsError.prototype);
    this.name = 'TipCalculationNoLaborForTicketsError';
    this.errors = errors;
  }
}

export interface INoLaborForTicketsResult {
  hasSkippableErrors: boolean;
  ticketsWithEmployee: ITipCalculatorEmployeeTicket[];
  ticketsWithoutEmployee: ITipCalculatorEmployeeTicket[];
}

/**
 * Parses the no labor for tickets error and returns the result.
 * @param errors - The no labor for tickets error.
 * @returns The result.
 */
export const parseNoLaborForTicketsError = (errors: TipCalculationNoLaborForTicketsError): INoLaborForTicketsResult => {
  const { ticketsWithEmployee, ticketsWithoutEmployee, hasSkippableErrors } = errors.errors.reduce(
    (acc, error) => {
      if (error.code === 'pos_no_labor_for_ticket') {
        if (error.allowSkip) acc.hasSkippableErrors = true;
        error.tickets?.forEach((ticket) => {
          if (ticket.employee) acc.ticketsWithEmployee.push(ticket);
          else acc.ticketsWithoutEmployee.push(ticket);
        });
      }
      return acc;
    },
    { ticketsWithEmployee: [], ticketsWithoutEmployee: [], hasSkippableErrors: false }
  );

  return { ticketsWithEmployee, ticketsWithoutEmployee, hasSkippableErrors };
};

export class TipCalculatorFriendlyError extends Error {
  public title: string;
  public summary: string;
  public items: ITipCalculatorFriendlyErrorItem[];

  constructor(title: string, summary: string, items: ITipCalculatorFriendlyErrorItem[]) {
    super(summary);
    Object.setPrototypeOf(this, TipCalculatorFriendlyError.prototype);
    this.name = 'TipCalculatorFriendlyError';
    this.title = title;
    this.summary = summary;
    this.items = items;
  }
}

export const buildTipCalcValidationError: Record<TipCalculationValidationType, { type: TipCalculationValidationType; title: string; message: string }> = {
  [TipCalculationValidationType.NO_TIPS_OR_AUTO_GRATUITIES_TO_DISTRIBUTE]: {
    type: TipCalculationValidationType.NO_TIPS_OR_AUTO_GRATUITIES_TO_DISTRIBUTE,
    title: 'No Tips or Auto Gratuities to Distribute',
    message: 'There were no tips or auto gratuities to distribute for this day.',
  },
  [TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES]: {
    type: TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES,
    title: 'Invalid Time Clock Entries',
    message: 'Please correct the time clock entries for the following employees. Employees must clock at least one minute of time when working a tipped job.',
  },
  [TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN]: {
    type: TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN,
    title: 'Employees Still Clocked In',
    message: 'Before performing tip calculations, all tippable employees must be clocked out. Please ensure the following employees have clocked and try again.',
  },
  [TipCalculationValidationType.EMPLOYEES_NO_BREAK_END]: {
    type: TipCalculationValidationType.EMPLOYEES_NO_BREAK_END,
    title: 'Employees Without Break End',
    message: 'Before performing tip calculations, all tippable employees must be clocked in from any breaks taken. Please ensure the following employees have completed their breaks.',
  },
  [TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES]: {
    type: TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES,
    title: 'No Eligible Employees',
    message: 'Based on your rules, there are no employees who worked matching your rules. Please check your configuration.',
  },
  [TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB]: {
    type: TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB,
    title: 'Tips Found for Jobs without Rules',
    message: 'No tip rules are defined for the jobs listed below. These employees may have clocked in with the wrong job code. Either correct the job code in your point of sale, or add a tip rule for these jobs.',
  },
  [TipCalculationValidationType.NO_RULE_FOR_JOB]: { type: TipCalculationValidationType.NO_RULE_FOR_JOB, title: 'Non-tipped Jobs', message: 'The following employees belong to jobs that do not benefit from the tip rules.' },
  [TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB]: { type: TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB, title: 'These Tips Will Be Ignored', message: 'These Tips Will Be Ignored' },
  [TipCalculationValidationType.NO_LABOR_FOR_TICKETS]: { type: TipCalculationValidationType.NO_LABOR_FOR_TICKETS, title: 'No Labor for Tickets', message: 'No Labor for Tickets' },
  [TipCalculationValidationType.NO_DATA]: {
    type: TipCalculationValidationType.NO_DATA,
    title: 'No Data',
    message: 'No data found for the selected date.',
  },
};

interface IResolvedTipCalculatorPathContext {
  employee?: ITipCalculatorRequest['employees'][number];
  ticket?: ITipCalculatorRequest['employees'][number]['tickets'][number];
  category?: NonNullable<ITipCalculatorRequest['employees'][number]['tickets'][number]['categories']>[number];
  remainingPath: string[];
}

// Resolves the context for the tip calculator request path.
const resolveRequestPathContext = (parts: string[], employeeIndex: number, request: ITipCalculatorRequest): IResolvedTipCalculatorPathContext | undefined => {
  const employee = request.employees?.[employeeIndex];
  if (!employee) return undefined;

  let ticket: IResolvedTipCalculatorPathContext['ticket'];
  let category: IResolvedTipCalculatorPathContext['category'];
  let cursorIndex = 2;

  if (parts[cursorIndex] === 'tickets') {
    const ticketIndex = Number(parts[cursorIndex + 1]);
    if (Number.isNaN(ticketIndex)) return undefined;

    ticket = employee.tickets?.[ticketIndex];
    if (!ticket) return undefined;

    cursorIndex += 2;

    if (parts[cursorIndex] === 'categories') {
      const categoryIndex = Number(parts[cursorIndex + 1]);
      if (Number.isNaN(categoryIndex)) return undefined;

      category = ticket.categories?.[categoryIndex];
      if (!category) return undefined;

      cursorIndex += 2;
    }
  }

  return { employee, ticket, category, remainingPath: parts.slice(cursorIndex) };
};

const resolveTipCalculatorPathContext = (param: string, tipCalcRequests: ITipCalculatorRequest[]): IResolvedTipCalculatorPathContext | undefined => {
  const parts = param.split('.');
  if (parts[0] !== 'employees') return undefined;

  const employeeIndex = Number(parts[1]);
  if (Number.isNaN(employeeIndex)) return undefined;

  return tipCalcRequests.reduce<IResolvedTipCalculatorPathContext | undefined>((resolved, request) => resolved || resolveRequestPathContext(parts, employeeIndex, request), undefined);
};

// Builds a friendly label for the key.
const keyToFriendlyLabel = (key?: string): string => {
  if (!key) return 'total';

  switch (key) {
    case 'gross':
      return 'total gross';
    case 'net':
      return 'total net';
    default:
      return key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .toLowerCase();
  }
};

// Checks if the remaining path refers to a non-currency value (e.g. minutes).
const isNonCurrencyField = (remainingPath: string[]): boolean => {
  const last = remainingPath[remainingPath.length - 1];
  return last === 'total_min_worked' || last === 'totalMinWorked';
};

// Formats the error value for display: minutes as "X minutes", currency as dollars.
const formatErrorValueDisplay = (value: number, remainingPath: string[]): string => (isNonCurrencyField(remainingPath) ? `${value} minutes` : centsToDollarsDisplay(value));

// Builds a single label for employee/ticket totals path, e.g. totals.tip.credit_card_fee -> "tip credit card fee"
const toFriendlyTotalsPathLabel = (remainingPath: string[]): string => {
  if (remainingPath[0] !== 'totals' || remainingPath.length < 2) return keyToFriendlyLabel(remainingPath[0]);
  const source = remainingPath[1];
  if (remainingPath.length >= 3) {
    return `${keyToFriendlyLabel(source)} ${keyToFriendlyLabel(remainingPath[2])}`;
  }
  return keyToFriendlyLabel(source);
};

// Processes the tip calculator error payload and returns a friendly error or raw errors.
export const processTipCalculatorError = (errorPayload: ITipCalculatorErrorPayload): TipCalculatorFriendlyError | IApiError[] | undefined => {
  // Support grouped errors so each error set can be mapped to the correct tipCalcRequest.
  if (!Array.isArray(errorPayload) && 'errorsByRequest' in errorPayload) {
    const friendlyItems: ITipCalculatorFriendlyErrorItem[] = [];
    const groupedRawErrors: IApiError[] = [];

    errorPayload.errorsByRequest.forEach((group) => {
      const friendlyError = buildTipCalculatorFriendlyError(group.errors, group.tipCalcRequests);
      if (friendlyError) friendlyItems.push(...friendlyError.items);
      else groupedRawErrors.push(...group.errors);
    });

    if (friendlyItems.length) {
      return new TipCalculatorFriendlyError('Some data may need to be corrected in your POS', 'Items in your tip data have validation errors. Please correct them in your POS and try again.', friendlyItems);
    }

    return groupedRawErrors;
  }

  const rawErrors: IApiError[] = Array.isArray(errorPayload) ? errorPayload : errorPayload.errors || [];
  const tipCalcRequests = Array.isArray(errorPayload) ? undefined : errorPayload.tipCalcRequests;
  const friendlyError = buildTipCalculatorFriendlyError(rawErrors, tipCalcRequests);

  return friendlyError || rawErrors;
};

// Builds a friendly error for the tip calculator error payload.
export const buildTipCalculatorFriendlyError = (errors: IApiError[], tipCalcRequests?: ITipCalculatorRequest[]): TipCalculatorFriendlyError | undefined => {
  if (!errors?.length || !tipCalcRequests?.length) return undefined;

  const items: ITipCalculatorFriendlyErrorItem[] = [];

  errors.forEach((error) => {
    const param = error?.param;
    const msg = error?.msg;
    if (!param || !msg) return;

    const context = resolveTipCalculatorPathContext(param, tipCalcRequests);
    if (!context) return;

    const { employee, ticket, category, remainingPath } = context;

    const isEmployeeIdMissingRequired = msg === 'is required' && /^employees\.\d+\.id$/.test(param);
    if (isEmployeeIdMissingRequired) {
      const employeeName = employee?.name || 'Employee';
      items.push({ ticketDisplayId: ticket?.displayId, employeeName, description: `Employee "${employeeName}" is missing an ID.` });
      return;
    }

    const numValue = Number(error?.value);

    if (Number.isNaN(numValue) || numValue >= 0) return;

    const valueDisplay = formatErrorValueDisplay(numValue, remainingPath);
    let description: string;

    if (ticket && category) {
      description = `Check #${ticket.displayId} for ${employee.name} has total for category "${category.name}" (${centsToDollarsDisplay(numValue)}) that ${error.msg}.`;
    } else {
      const label = remainingPath[0] === 'totals' ? toFriendlyTotalsPathLabel(remainingPath) : keyToFriendlyLabel(remainingPath[0]);
      const subject = ticket ? `Check #${ticket.displayId} for ${employee.name}` : employee.name;
      description = `${subject} has ${label} amount (${valueDisplay}) that ${error.msg}.`;
    }

    items.push({ ticketDisplayId: ticket?.displayId, employeeName: employee.name, description });
  });

  if (!items.length) return undefined;

  return new TipCalculatorFriendlyError('Some data may need to be corrected in your POS', 'Items in your tip data have validation errors. Please correct them in your POS and try again.', items);
};

interface IRuleEligibleJobTitles {
  contributingJobTitles: Set<string>;
  benefittingJobTitles: Set<string>;
  noShareZeroPointJobTitles: Set<string>;
  virtualContributorJobTitles: Set<string>;
  ownerRuleProviderIds: Set<string>;
}

// Get a list of contributing and benefitting job titles from calculation rules
// This includes contributors, additional contributors, share beneficiaries, and remainder beneficiaries
export const getRuleEligibleJobTitles = (calcRules: ICalculatorRule[]): IRuleEligibleJobTitles => {
  const contributingJobTitles = new Set<string>();
  const benefittingJobTitles = new Set<string>();
  const noShareZeroPointJobTitles = new Set<string>();
  const virtualContributorJobTitles = new Set<string>();
  const ownerRuleProviderIds = new Set<string>();

  calcRules.forEach((rule) => {
    if (rule.sharePolicy) {
      const contributors = [rule.contributor, ...(rule.additionalContributors || [])];
      const shareBeneficiaries = rule.sharePolicy.shares?.flatMap((s) => s.beneficiaries || []) || [];
      const remainderBeneficiaries = rule.sharePolicy.remainder?.beneficiaries?.flatMap((b) => b.participants || []) || [];

      // Add contributing job titles
      contributors.forEach((p) => {
        if (p?.type === CalculatorParticipantType.JOB) {
          const jobTitle = normalizeJobTitle(p.name);
          if (jobTitle) {
            contributingJobTitles.add(jobTitle);
            if (rule.isVirtual) virtualContributorJobTitles.add(jobTitle);
          }
        }
      });

      // Add benefitting job titles
      [...shareBeneficiaries, ...remainderBeneficiaries].forEach((p) => {
        if (p?.name) benefittingJobTitles.add(normalizeJobTitle(p?.name));
      });
    }

    // Check if the default pool rule includes no labor
    // If so, add the default pool unknown job title to the contributing job titles
    if (rule.default && rule.includeNoLabor) {
      contributingJobTitles.add(normalizeJobTitle(DEFAULT_POOL_UNKNOWN_JOB_TITLE));
    }

    // Check if the rule has a zero point value and no share policy
    // If so, add job titles that have a zero point value and no share policy to the no share zero point job titles
    if (!rule.sharePolicy && rule.value === 0) {
      noShareZeroPointJobTitles.add(normalizeJobTitle(rule.contributor?.name));
    }

    // Add provider ids for owner rules (rules with an EMPLOYEE-typed contributor)
    if (rule.contributor?.type === CalculatorParticipantType.EMPLOYEE && rule.contributor.providerId) {
      ownerRuleProviderIds.add(rule.contributor.providerId);
    }
  });

  return {
    contributingJobTitles,
    benefittingJobTitles,
    noShareZeroPointJobTitles,
    virtualContributorJobTitles,
    ownerRuleProviderIds,
  };
};

/**
 * Determines whether a POS segment should be surfaced as an entry on the Tip Data page.
 *
 * An entry is eligible when either:
 * - its job participates in a share policy (matching the default-pool flag for the segment), or
 * - the provider is covered by an owner rule (EMPLOYEE-typed contributor).
 */
export const isTipDataSegmentEntryEligible = (segment: IPOSSegment, providerEmployeeId: string, sharePolicyJobs: { title: string; default: boolean }[], ruleEligibleJobTitles: IRuleEligibleJobTitles): boolean => {
  const jobTitle = segment.job.title.toLowerCase();
  const isDefaultPoolSegment = segment.id === DEFAULT_POOL_ID;

  const matchesSharePolicyJob = sharePolicyJobs.some((item) => item.title.toLowerCase() === jobTitle && item.default === isDefaultPoolSegment);
  const isOwnerRuleProvider = ruleEligibleJobTitles.ownerRuleProviderIds.has(providerEmployeeId);

  return matchesSharePolicyJob || isOwnerRuleProvider;
};

/**
 * Finds the calculator rule that has an EMPLOYEE-typed contributor tied to the given provider id.
 *
 * @param {ICalculatorRule[]} calculatorRules - The list of calculator rules to inspect.
 * @param {string} [providerId] - The employee's POS provider id to match against rule contributors.
 * @returns {ICalculatorRule | undefined} The owner rule for the given provider id, or undefined if none matches.
 */
export const getOwnerRule = (calculatorRules?: ICalculatorRule[], providerId?: string): ICalculatorRule | undefined => {
  if (!calculatorRules || !providerId) return undefined;
  return calculatorRules.find((r) => r.contributor?.type === CalculatorParticipantType.EMPLOYEE && r.contributor.providerId === providerId);
};

/**
 * Collects the POS provider ids covered by an owner rule (EMPLOYEE-typed contributor).
 *
 * @param {ICalculatorRule[]} calculatorRules - The list of calculator rules to inspect.
 * @returns {Set<string>} The provider ids tied to owner rules.
 */
export const getOwnerRuleProviderIds = (calculatorRules: ICalculatorRule[] = []): Set<string> =>
  calculatorRules.reduce((ids, rule) => {
    if (rule.contributor?.type === CalculatorParticipantType.EMPLOYEE && rule.contributor.providerId) {
      ids.add(rule.contributor.providerId);
    }
    return ids;
  }, new Set<string>());

/**
 * Determines the job-title subtext to display for a tip-data payee entry.
 *
 * A missing or empty job title is always hidden. Otherwise non owner-rule
 * payees always show their job title, while owner-rule payees only show one
 * when it carries real labor data — i.e. it is not the "unknown" placeholder
 * and is not merely an echo of the payee's own name.
 *
 * @param isOwnerRule whether the payee is covered by an owner rule
 * @param displayName the name shown as the entry title (employee or provider name), if any
 * @param jobTitle    the raw job title from the matched segment, if any
 * @returns the title-cased job title, or undefined when it should be hidden
 */
export const formatTipDataJobTitle = (isOwnerRule: boolean, displayName?: string, jobTitle?: string): string | undefined => {
  const normalizedJob = normalizeJobTitle(jobTitle || '');

  // No job title to show (missing or empty).
  if (!normalizedJob) return undefined;

  // Owner-rule payees with no labor data have a job title that is either the
  // "unknown" placeholder or just their own name — hide it in those cases.
  // Everyone else (and owner-rule payees with real labor data) shows it.
  if (isOwnerRule && (normalizedJob === normalizeJobTitle(displayName || '') || normalizedJob === normalizeJobTitle(DEFAULT_POOL_UNKNOWN_JOB_TITLE))) {
    return undefined;
  }

  return toTitleCase(jobTitle);
};

// Build the employees array for the tip calculator request.
export const buildTipCalculatorEmployees = (integrationData: IPOSResults, calcRules: ICalculatorRule[], segment: IPOSResultSegment): ITipCalculatorRequestEmployee[] => {
  // Check if the default pool rule includes no labor
  const includeNoLabor = calcRules.some((rule) => rule.default && rule.includeNoLabor);

  // Get a list of benefitting and contributing job titles from the calculation rules
  const ruleEligibleJobTitles = getRuleEligibleJobTitles(calcRules);

  // Helper function to check if an employee is eligible based on their job title
  const checkIsJobEligible = (jobTitle: string) => ruleEligibleJobTitles.contributingJobTitles.has(jobTitle) || ruleEligibleJobTitles.benefittingJobTitles.has(jobTitle);

  // Helper function to find matching owner rules
  const checkIsOwnerRule = (providerId: string) => ruleEligibleJobTitles.ownerRuleProviderIds.has(providerId);

  const results: ITipCalculatorRequestEmployee[] = integrationData.data.reduce((acc, data) => {
    // Owner-rule status is a per-provider fact, so evaluate it once per provider rather than per segment.
    const isOwnerRule = checkIsOwnerRule(data.provider.employeeId);
    data.segments.forEach((segmentData) => {
      if (segmentData.id === segment.id) {
        const jobTitle = normalizeJobTitle(segmentData.job.title);
        const isJobEligible = checkIsJobEligible(jobTitle);
        if (isJobEligible || isOwnerRule) {
          acc.push({
            id: data.employee?.id || data.provider.employeeId, // use the kickfin employee id if present
            name: data.employee?.name || data.provider.name,
            job: { title: jobTitle },
            provider: { id: data.provider.employeeId, type: data.provider.type },
            totals: { ...segmentData.totals, totalMinWorked: segmentData.totals?.totalMinWorked || 0 },
            tickets: segmentData.tickets,
            schedules: isOwnerRule ? [] : matchingJobSchedules(segmentData.job.title, data.provider.schedules, segment, ruleEligibleJobTitles, includeNoLabor), // Owner rules do not require a valid schedule, so we set to an empty array.
          });
        }
      }
    });
    return acc;
  }, []);

  return results;
};

// Validates the tip rules vs the labor data
export const validateTipRulesAgainstLaborData = (integrationData: IPOSResults, calcRules: ICalculatorRulesSource, tipCalcRequests: ITipCalculatorRequest[]): void => {
  const errors: ITipCalcValidationError[] = []; // Array of errors returned.

  // validate the data array contains integration data otherwise return an error
  if (integrationData.data.length === 0) throw new TipCalculationNoDataError('No data found for the selected date.');

  // Get a list of contributing and benefiting job titles from the calculation rules
  const ruleEligibleJobTitles = getRuleEligibleJobTitles(calcRules.rules);

  // Validate the tips and auto gratuity collected for the integration data
  // This method will throw an error if the tips or auto gratuity collected is 0.
  validateTipsAndAutoGratuityCollected(integrationData);

  // Validate the schedules for the integration data
  // This method will throw an error if the schedules are invalid
  validateEmployeeSchedules(integrationData, ruleEligibleJobTitles);

  // Validate the tip share rules for the integration data
  // This method will throw an error when an employee has earned tips/autograts but no tip share rule is defined for their job title.
  validateTipShareRules(integrationData, ruleEligibleJobTitles);

  // Validate the zero point rules for the integration data
  // This method will throw an error when a tip rule has zero points and the job is not configured to accept tips.
  const zeroPointRules = validateZeroPointRules(integrationData, ruleEligibleJobTitles);
  if (zeroPointRules) errors.push(zeroPointRules);

  // Validate the no rules for jobs for the integration data
  // This method will throw an error when an employee has earned tips/autograts but no rule is defined for their job title.
  const noRulesForJobs = validateNoRulesForJobs(integrationData, ruleEligibleJobTitles);
  if (noRulesForJobs) errors.push(noRulesForJobs);

  // Check if there are employees to be tipped
  if (!tipCalcRequests.some((req) => req.employees.length > 0)) {
    errors.push(buildTipCalcValidationError[TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES]);
  }

  if (errors.length) throw new TipCalculationValidationError('tip calculation validation error', errors);
};

/**
 * Validates that tips and auto gratuity collected are greater than 0.
 *
 * Purpose: Prevent running calculations when no tips or auto gratuity collected are found.
 *
 * What it checks:
 * - Gross collected: sum of all tip.gross and autoGratuity.gross from all segments
 * - Net collected: sum of all tip.net and autoGratuity.net from all segments
 *
 * Behavior:
 * - Throws TipCalculationValidationError with TipCalculationValidationType.NO_TIPS_OR_AUTO_GRATUITY_COLLECTED when both gross and net collected are 0.
 */
const validateTipsAndAutoGratuityCollected = (integrationData: IPOSResults): void => {
  // Aggregate the gross and net collected amounts from all segments
  const { grossCollected, netCollected } = integrationData.data.reduce(
    (totals, posData) => ({
      grossCollected: totals.grossCollected + (posData.totals.tip.gross || 0) + (posData.totals.autoGratuity.gross || 0),
      netCollected: totals.netCollected + (posData.totals.tip.net || 0) + (posData.totals.autoGratuity.net || 0),
    }),
    { grossCollected: 0, netCollected: 0 }
  );

  // If the gross and net collected amounts are 0, throw an error
  if (grossCollected === 0 && netCollected === 0) {
    throw new TipCalculationValidationError('tip calculation validation error', [buildTipCalcValidationError[TipCalculationValidationType.NO_TIPS_OR_AUTO_GRATUITIES_TO_DISTRIBUTE]]);
  }
};

/**
 * Validates time clock schedules for employees who are eligible to benefit from tip rules.
 *
 * Purpose: Prevent running tip calculations when tipped/benefitting employees
 * have incomplete or invalid clock data that would produce incorrect payouts or confusing results.
 *
 * What it checks (only for benefitting job titles):
 * - Invalid shift times: schedule.end is same as or before schedule.start
 * - Still clocked in: schedule has no end time
 * - Break not ended: any break entry is missing an end time
 *
 * Exemption:
 * - Employees who have an owner rule (EMPLOYEE-typed contributor) are skipped, as their payout
 *   is not derived from their clock data.
 *
 * Behavior:
 * - Aggregates affected employees (and job titles where relevant)
 * - Throws TipCalculationValidationError with a specific TipCalculationValidationType
 *   and a user-facing message describing how to fix the issue
 * - Returns undefined when all validations pass
 */
const validateEmployeeSchedules = (posResults: IPOSResults, ruleEligibleJobTitles: IRuleEligibleJobTitles): void => {
  const invalidEmployeeSchedule: { name: string; jobTitle: string; start: string; end: string; totalMinWorked: number }[] = []; // Shifts with end <= start
  const noScheduleEnd: { title: string; total: number; employees: { name: string; total: number }[] }[] = []; // Employees still clocked in (missing shift end), grouped by job title
  const noBreakEnd: { title: string; total: number; employees: { name: string; total: number }[] }[] = []; // Employees still on break (missing break end), grouped by job title

  posResults.data.forEach((data) => {
    // Skip validation for employees who have an owner rule
    if (ruleEligibleJobTitles.ownerRuleProviderIds.has(data.provider.employeeId)) return;

    data.provider.schedules.forEach((schedule) => {
      const jobTitle = normalizeJobTitle(schedule.jobTitle);

      // Do not check schedules for sharePOS jobs
      if (ruleEligibleJobTitles.virtualContributorJobTitles.has(jobTitle)) return;

      const isBenefittingJob = ruleEligibleJobTitles.benefittingJobTitles.has(jobTitle);
      const isContributingJobWithTips = ruleEligibleJobTitles.contributingJobTitles.has(jobTitle) && data.totals.net > 0;

      // Apply validations only for schedules whose job title is a benefitting job title.
      if (isBenefittingJob || isContributingJobWithTips) {
        // TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN
        // Incomplete shift: no end time (employee is still clocked in).
        if (!schedule.end) {
          const job = noScheduleEnd.find((item) => item.title.toLowerCase() === jobTitle);
          if (job) {
            job.employees.push({ name: data.provider.name, total: 0 });
          } else {
            noScheduleEnd.push({ title: jobTitle, total: 0, employees: [{ name: data.provider.name, total: 0 }] });
          }
        } else {
          // TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES
          // Invalid shift: end time is the same as or before the start time.
          if (schedule.start && schedule.end && dayjs(schedule.end).isSameOrBefore(dayjs(schedule.start))) {
            invalidEmployeeSchedule.push({ name: data.provider.name, jobTitle: schedule.jobTitle, start: schedule.start, end: schedule.end, totalMinWorked: schedule.totalMinWorked });
          }

          // Apply validations only for schedules whose job title is a benefitting job title.
          if (isBenefittingJob) {
            // TipCalculationValidationType.EMPLOYEES_NO_BREAK_END
            // Incomplete break: at least one break has no end time (employee is still on break).
            if (schedule.breaks.some((item) => !item.end)) {
              const job = noBreakEnd.find((item) => item.title.toLowerCase() === jobTitle);
              if (job) {
                job.employees.push({ name: data.provider.name, total: 0 });
              } else {
                noBreakEnd.push({ title: jobTitle, total: 0, employees: [{ name: data.provider.name, total: 0 }] });
              }
            }
          }
        }
      }
    });
  });

  // Throw priority: we return the first “bucket” of issues we find, so the user can fix problems in a sensible order.
  if (invalidEmployeeSchedule.length > 0) {
    throw new TipCalculationValidationError('tip calculation validation error', [
      {
        ...buildTipCalcValidationError[TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES],
        employees: invalidEmployeeSchedule,
      },
    ]);
  }

  if (noScheduleEnd.length > 0) {
    throw new TipCalculationValidationError('tip calculation validation error', [
      {
        ...buildTipCalcValidationError[TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN],
        jobs: noScheduleEnd,
      },
    ]);
  }

  if (noBreakEnd.length > 0) {
    throw new TipCalculationValidationError('tip calculation validation error', [
      {
        ...buildTipCalcValidationError[TipCalculationValidationType.EMPLOYEES_NO_BREAK_END],
        jobs: noBreakEnd,
      },
    ]);
  }

  return undefined;
};

/**
 * Validates that tip share rules exist for any job that has employees who earned tips/autograts.
 *
 * Purpose:
 * - Prevent running calculations when the POS reports tip earnings for a job that is not covered by any configured rule.
 * - This usually indicates either:
 *   - the employee clocked in under an unexpected job code, or a rule needs to be added for that job.
 *
 * Behavior:
 * - Scans POS segments and finds tipped earnings (\(segment.totals.net > 0\)) for job titles that are not explicitly excluded.
 * - Aggregates results by job title and includes employee names + amounts.
 * - Throws TipCalculationValidationError with TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB when any are found.
 */
const validateTipShareRules = (posResults: IPOSResults, ruleEligibleJobTitles: IRuleEligibleJobTitles): void => {
  const jobsWithoutShareRule: { title: string; total: number; employees: { name: string; total: number }[] }[] = []; // Unique job titles with tip earnings but no matching rule

  // Loop through POS segments and find any with tip earnings where the job title is not in the list of contributing or benefitting job titles, and not in the list of no-share zero point job titles.
  posResults.data.forEach((data) => {
    // Skip validation for employees who have an owner rule, as they are not required to have a share rule.
    if (ruleEligibleJobTitles.ownerRuleProviderIds.has(data.provider.employeeId)) return;

    data.segments.forEach((segment) => {
      const jobTitle = normalizeJobTitle(segment.job.title);

      // A segment with tips/autograts must map to a rule, unless the job is explicitly excluded from sharing.
      if (segment.totals.net > 0 && !ruleEligibleJobTitles.contributingJobTitles.has(jobTitle) && !ruleEligibleJobTitles.noShareZeroPointJobTitles.has(jobTitle)) {
        // Group by job title so the UI can show “Job X is missing a rule” with affected employees and totals.
        const job = jobsWithoutShareRule.find((item) => item.title.toLowerCase() === jobTitle);
        if (job) {
          job.total += segment.totals.net;
          job.employees.push({ name: data.provider.name, total: segment.totals.net });
        } else {
          jobsWithoutShareRule.push({ title: segment.job.title.toLowerCase(), total: segment.totals.net, employees: [{ name: data.provider.name, total: segment.totals.net }] });
        }
      }
    });
  });

  if (jobsWithoutShareRule.length > 0) {
    // Block calculation and instruct the user to either fix job codes in the POS or add missing rules.
    throw new TipCalculationValidationError('tip calculation validation error', [
      {
        ...buildTipCalcValidationError[TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB],
        jobs: jobsWithoutShareRule,
      },
    ]);
  }
};

/**
 * Validates that jobs marked as "no share / zero point" are not generating tips.
 *
 * Product intent (simple): these jobs are configured to receive **0** points, so any tips recorded under these job codes
 * will be ignored by the platform. This warning helps catch "wrong job code" scenarios before users proceed.
 *
 * Technical behavior:
 * - Looks for segments with a positive tip total (`segment.totals.net > 0`)
 * - Where the job title is in `noShareZeroPointJobTitles`
 * - Groups results by job title and aggregates by employee for display.
 *
 * @returns A structured validation error (used by the UI) or `undefined` if no issues are found.
 */
const validateZeroPointRules = (posResults: IPOSResults, ruleEligibleJobTitles: IRuleEligibleJobTitles): ITipCalcValidationError | undefined => {
  const zeroPointsRuleForJob: { title: string; total: number; employees: { name: string; total: number }[] }[] = [];

  posResults.data.forEach((data) => {
    data.segments.forEach((segment) => {
      const jobTitle = normalizeJobTitle(segment.job.title);

      // "No share / zero point" jobs should not have tips; if they do, those tips will be ignored.
      if (segment.totals.net > 0 && ruleEligibleJobTitles.noShareZeroPointJobTitles.has(jobTitle)) {
        const job = zeroPointsRuleForJob.find((item) => item.title.toLowerCase() === jobTitle);
        if (job) {
          job.total += segment.totals.net;
          const employee = job.employees.find((employee) => employee.name === data.provider.name);
          if (!employee) {
            job.employees.push({ name: data.provider.name, total: segment.totals.net });
          } else {
            employee.total += segment.totals.net;
          }
        } else {
          zeroPointsRuleForJob.push({ title: jobTitle, total: segment.totals.net, employees: [{ name: data.provider.name, total: segment.totals.net }] });
        }
      }
    });
  });

  if (zeroPointsRuleForJob.length > 0) {
    return {
      ...buildTipCalcValidationError[TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB],
      jobs: zeroPointsRuleForJob,
    };
  }

  return undefined;
};

/**
 * Validates that every job present in the POS data is accounted for by the current tip rules.
 *
 * Product intent: if someone worked a job that isn't recognized by the tip rules, they may have logged hours but
 * receive $0 from tip sharing. We surface this as a warning so the user can fix POS job codes or update their rules.
 *
 * Technical behavior:
 * - Scans every provider segment from the POS integration.
 * - Normalizes the job title (case/whitespace) to match how rules are stored.
 * - Flags segments whose job title is NOT present in any of these rule-derived lists:
 *   - contributing jobs
 *   - shared POS contributing jobs
 *   - benefitting jobs (eligible to receive tips)
 *   - no-share / zero-point excluded jobs
 * - Groups flagged segments by job title, aggregating:
 *   - total net across all segments for that job
 *   - per-employee net totals for affected employees
 *
 * @returns A structured validation error for the UI, or `undefined` if no issues are found.
 */
const validateNoRulesForJobs = (posResults: IPOSResults, ruleEligibleJobTitles: IRuleEligibleJobTitles): ITipCalcValidationError | undefined => {
  const noRulesForJobs: { title: string; total: number; employees: { name: string; total: number }[] }[] = [];

  posResults.data.forEach((data) => {
    // Owner rules apply to the whole provider, so this is constant across their segments — compute it once.
    const isOwnerRule = ruleEligibleJobTitles.ownerRuleProviderIds.has(data.provider.employeeId);

    data.segments.forEach((segment) => {
      // Normalize to avoid case-mismatch issues (rules are stored as lowercase job titles elsewhere).
      const jobTitle = normalizeJobTitle(segment.job.title);

      const isContributingJob = ruleEligibleJobTitles.contributingJobTitles.has(jobTitle);
      const isBenefittingJob = ruleEligibleJobTitles.benefittingJobTitles.has(jobTitle);
      const isNoShareZeroPointJob = ruleEligibleJobTitles.noShareZeroPointJobTitles.has(jobTitle);
      const isVirtualContributorJob = ruleEligibleJobTitles.virtualContributorJobTitles.has(jobTitle);

      // if the job is not a contributing job, benefitting job, no share zero point job, not an owner rule, or virtual contributor job, it is unruled.
      if (!isContributingJob && !isBenefittingJob && !isNoShareZeroPointJob && !isOwnerRule && !isVirtualContributorJob) {
        // Group by job title so the UI can say “Job X has no rule” with a list of impacted employees.
        const job = noRulesForJobs.find((item) => item.title.toLowerCase() === jobTitle);
        if (job) {
          // Aggregate totals across all segments for this job title.
          job.total += segment.totals.net;

          // Aggregate per-employee totals (a single employee can appear in multiple segments for the same job).
          const employee = job.employees.find((employee) => employee.name === data.provider.name);
          if (!employee) {
            job.employees.push({ name: data.provider.name, total: segment.totals.net });
          } else {
            employee.total += segment.totals.net;
          }
        } else {
          noRulesForJobs.push({ title: jobTitle, total: segment.totals.net, employees: [{ name: data.provider.name, total: segment.totals.net }] });
        }
      }
    });
  });

  if (noRulesForJobs.length > 0) {
    return {
      ...buildTipCalcValidationError[TipCalculationValidationType.NO_RULE_FOR_JOB],
      jobs: noRulesForJobs,
    };
  }

  return undefined;
};

// Match provider jobs to schedule
const matchingJobSchedules = (job: string, schedules: IPOSProviderSchedule[], segment: IPOSResultSegment, ruleEligibleJobTitles: IRuleEligibleJobTitles, includeNoLabor?: boolean): ITipCalculatorRequestEmployeeSchedule[] => {
  const jobTitle = normalizeJobTitle(job);
  if (includeNoLabor && segment?.name.toLowerCase() === DEFAULT_POOL_NAME.toLowerCase()) return [];

  // If the job is NOT benefiting job, return an empty array.
  // This ensures employees without a role in tip sharing rules do not have schedules,
  // which can prevent errors in the calculator for invalid time entries.

  if (!ruleEligibleJobTitles.benefittingJobTitles.has(jobTitle)) return [];

  const matchingSchedules = schedules.filter((schedule) => schedule.jobTitle.toLowerCase().trim() === jobTitle);
  return matchingSchedules.map((schedule) => ({ start: schedule.start, end: schedule.end }));
};

// Build the tip calculator requests.
// This method returns an array of requests for the tip calculator for each segment.
export const buildTipCalculatorRequests = (posData: IPOSResults, calcRules: ICalculatorRulesSource, segments: IDaySegment[]): ITipCalculatorRequest[] => {
  // Get list of unique segments from POS results
  const uniqueSegments = getSortedUniqueSegments(posData, segments);

  let includeParticipants = false;
  // When participants are defined in the rules, check if any rule or split check is benefitting any of the types defined.
  // In this case, we send the participants along with the request to calculate the participant totals.
  if (calcRules.participants?.length > 0) {
    const participantTypes = calcRules.participants.map((participant) => participant.type);
    includeParticipants = calcRules.rules.some((rule) => rule.sharePolicy?.shares?.some((share) => share.beneficiaries.some((beneficiary) => participantTypes.includes(beneficiary.type))));

    if (!includeParticipants) {
      includeParticipants = posData.data.some((data) =>
        data.segments.some((segment) =>
          segment.tickets.some((ticket) => {
            if (ticket.override?.type === TicketOverrideType.SPLIT) {
              return ticket.override.shares.some((share) => participantTypes.includes(share.participant.type));
            }
            return false;
          })
        )
      );
    }
  }
  // Build tip calculator requests for segments
  const data = uniqueSegments?.map((segment) => ({
    segmentId: segment.id,
    segment: segment.name,
    start: segment.start || '--:--',
    end: segment.end || '--:--',
    rules: calcRules.rules,
    employees: buildTipCalculatorEmployees(posData, calcRules.rules, segment),
    categoryGroups: calcRules.categoryGroups,
    ...(includeParticipants && { participants: calcRules.participants }),
  }));

  // Check if the total tips for the day is greater than 0.
  const results: ITipCalculatorRequest[] = data.filter((item) => {
    const totalNet = sumBy(item.employees, 'totals.net'); // sum total net earned for the day
    if (totalNet > 0) return true;
    // If the amount equals 0, we remove the processing of the segment, unless that segment also includes split checks
    const hasSplitChecks = item.employees?.some((emp) => emp.tickets?.some((ticket) => ticket.override?.type === TicketOverrideType.SPLIT));
    return totalNet === 0 && hasSplitChecks;
  });

  return results;
};

interface IGroupedEmployeeResult {
  calculations: ITipCalculatorResultEmployee[];
  gross: number;
  autoGratuity: number;
}

/**
 * Groups and aggregates tip calculation results by employee across multiple segments.
 * It also calculates the total gross and autoGratuity for each employee by summing their totals from all segments, including split checks.
 *
 * @param tipCalcResults - An array of tip calculation results for each segment.
 * @returns A map of aggregated results per employee, containing total `gross`, `autoGratuity`, and an array of their `calculations`.
 */
const groupCalculatedResultsByEmployee = (tipCalcResults: ITipCalculatorResult[]): Map<string, IGroupedEmployeeResult> => {
  const employeeResultsMap = new Map<string, IGroupedEmployeeResult>();
  tipCalcResults.forEach((result) => {
    result?.employees?.forEach((employeeResult) => {
      const { id } = employeeResult;
      if (!employeeResultsMap.has(id)) employeeResultsMap.set(id, { calculations: [], gross: 0, autoGratuity: 0 });

      const groupedResult = employeeResultsMap.get(id)!;
      groupedResult.calculations.push({ ...employeeResult, segmentId: result.segmentId });
      groupedResult.gross += employeeResult.calculated.total;
      groupedResult.autoGratuity += employeeResult.calculated.autoGratuities;

      employeeResult.splitChecks?.received?.forEach((check) => {
        groupedResult.gross += check.totals.total;
        groupedResult.autoGratuity += check.totals.autoGratuities;
      });
    });
  });
  return employeeResultsMap;
};

// Build TipCalculatorPayees from POS data
export const buildTipCalculatorPayeeFromPOSData = (tipCalcResults: ITipCalculatorResult[], posResults: IPOSResults, site: ISite, payrollTips?: IEmployeePayrollTip[]): ITipCalculatorPayee[] => {
  const payees: ITipCalculatorPayee[] = [];
  const payrollMap = new Map(siteUsesWithholdings(site) ? payrollTips?.map((p) => [p.employee.id, p.payroll.total]) : []);
  const employeeResultsMap = groupCalculatedResultsByEmployee(tipCalcResults);

  // Assemble the payee by iterating through the labor/ticket data.
  // "Other Contributors" (collected gross from POS but received nothing back) are naturally
  // excluded by the `gross > 0` check below — calculator results never assign them a gross.
  posResults.data.forEach(({ employee, provider, segments }) => {
    const { calculations, gross, autoGratuity } = employeeResultsMap.get(employee?.id || provider.employeeId) || {};

    if (gross > 0) {
      const payeeTotals = calculatePayeeTotals(gross, site, employee?.disbursementMethod, employee?.withholdings, payrollMap.get(employee?.id));

      const payee: ITipCalculatorPayee = {
        id: employee?.id || provider.employeeId,
        name: employee?.name || provider.name,
        calculations,
        ...payeeTotals,
        autoGratuity,
        segments,
        provider,
        employee,
      };

      payees.push(payee);
    }
  });

  return payees;
};

/**
 * Calculates the holdback amount for a payee after accounting for cash payouts and manual adjustments.
 *
 * It first checks for any manual holdback adjustments in the change log. If a manual adjustment is found,
 * that value is used. Otherwise, it calculates holdback based on site settings.
 *
 * @param {ITipCalculatorPayee} payee - The payee for whom to calculate the holdback.
 * @param {IHoldbackFixed | IHoldbackPercent} siteHoldbackSettings - The site's holdback configuration.
 * @param {ITipCalculatorChangeLogEntry[]} changeLogEntries - A log of changes, used to find manual holdback adjustments.
 * @returns {number} The calculated holdback amount.
 */

export const calculateHoldbackAfterCash = (payee: ITipCalculatorPayee, siteHoldbackSettings: IHoldbackFixed | IHoldbackPercent, changeLogEntries: ITipCalculatorChangeLogEntry[]) => {
  const { id, employee, gross, withholdingAmount, cash = 0 } = payee;
  const withheldGross = gross - withholdingAmount;

  // Set the holdbackAmount to 0 when is not INSTANT or the cash payout is equal to the gross amount
  if (employee?.disbursementMethod !== DisbursementMethod.INSTANT || cash === withheldGross) return 0;

  let holdbackAmount: number;

  // Get the latest payee holdback change log entry by iterating backwards so we can restore the holdbackAmount to the change log entry value
  const holdBackEntry = findLast(changeLogEntries, (entry) => entry.participant.type === ChangeLogParticipantType.EMPLOYEE && entry.details.id === id && entry.action === ChangeLogAction.HOLDBACK_EDITED);
  const restoredHoldback = holdBackEntry?.details?.to;

  if (typeof restoredHoldback === 'number') {
    holdbackAmount = restoredHoldback;
  } else {
    holdbackAmount = calculateHoldbackValue(employee.disbursementMethod, siteHoldbackSettings, withheldGross - cash);
  }

  return holdbackAmount;
};

/**
 * Builds a list of participants grouped by name and type from the given tip calculation results.
 *
 * @param {ITipCalculatorResult[]} tipCalcResults - An array of tip calculation results, where each result contains:
 *  - `segment`: The segment (e.g., "morning", "night").
 *  - `segmentId`: The ID associated with the segment.
 *  - `participants`: An array of participant objects to process.
 * @returns {ITipCalculatorParticipant[]} An array of participants, grouped by name and type, with aggregated calculations.
 */
export const buildTipCalculatorParticipants = (tipCalcResults: ITipCalculatorResult[]): ITipCalculatorParticipant[] => {
  const participants: ITipCalculatorParticipant[] = [];

  tipCalcResults.forEach((result) => {
    result.participants?.forEach((participant) => {
      const existingParticipant = participants.find((p) => p.name.toLowerCase().trim() === participant.name.toLowerCase().trim() && p.type === participant.type);
      let gross = participant.calculated.total;
      let autoGratuity = participant.calculated.autoGratuities;

      // Add split check totals
      participant.splitChecks?.received.forEach((received) => {
        gross += received.totals.total;
        autoGratuity += received.totals.autoGratuities;
      });

      if (existingParticipant) {
        existingParticipant.gross += gross;
        existingParticipant.net += gross;
        existingParticipant.autoGratuity += autoGratuity;
        existingParticipant.calculations.push({ ...participant, name: result.segment, id: result.segmentId });
      } else {
        participants.push({
          name: participant.name,
          type: participant.type,
          calculations: [{ ...participant, name: result.segment, id: result.segmentId }],
          gross,
          net: gross,
          autoGratuity,
        });
      }
    });
  });

  // only show participants with positive received amounts
  return participants.filter((participant) => participant.gross > 0);
};

/**
 * Build Other Contributor's breakdown: one calculator row per segment with gross tips, matched by contributor id and job title, tagged with its segmentId.
 *
 * @param posData - The contributor's POS data (provider/employee and segments).
 * @param tipCalcResults - Per-segment calculator results.
 * @returns One row per segment where the contributor has a matching entry.
 */
export const buildContributorSegmentCalculations = (posData: IPOSData, tipCalcResults: ITipCalculatorResult[]): ITipCalculatorResultEmployee[] => {
  const id = posData.employee?.id || posData.provider.employeeId;

  return (posData.segments || []).reduce<ITipCalculatorResultEmployee[]>((acc, segment) => {
    if (segment.totals.gross > 0) {
      const segmentJobTitle = normalizeJobTitle(segment.job.title);
      const segmentResult = tipCalcResults.find((result) => result.segmentId === segment.id);
      const calculation = segmentResult?.employees?.find((employee) => employee.id === id && normalizeJobTitle(employee.job.title) === segmentJobTitle);
      if (calculation) acc.push({ ...calculation, segmentId: segment.id });
    }
    return acc;
  }, []);
};

export const isDatePayable = (date: Dayjs, offset: number, timezone: string, daySegments?: IDaySegment[], currentTime?: Dayjs): boolean => {
  if (!date) return false;
  const now = currentTime ? dayjs(currentTime).tz(timezone) : dayjs().tz(timezone);

  // check if is end of last segment
  if (daySegments && daySegments.length > 0) {
    const selectedDayName = date.format('dddd').toLocaleUpperCase() as Days;

    // get valid day segments
    const validDaySegments = daySegments.filter((segment) => selectedDayName && segment?.days.includes(selectedDayName));

    if (validDaySegments.length > 0) {
      const lastDaySegment = validDaySegments.sort((a: IDaySegment, b: IDaySegment) => {
        const start = parseInt(a.start.split(':').join(''), 10);
        const end = parseInt(b.start.split(':').join(''), 10);
        return start - end;
      })[validDaySegments.length - 1];

      let endLastSegment = dayjs.tz(`${date.format('YYYY-MM-DD')} ${lastDaySegment?.end}`, timezone);
      if (lastDaySegment?.endsNextDay) endLastSegment = endLastSegment.add(1, 'day');

      return now >= endLastSegment;
    }
  }

  const morningOffset = 21600; // 6:00 am - assume close is after midnight (next day)
  let businessDayEnd = dayjs.tz(date, timezone).startOf('day').add(offset, 'seconds');
  if (offset <= morningOffset) businessDayEnd = businessDayEnd.add(1, 'day');

  return now >= businessDayEnd;
};

/**
 * Builds the payment set calculations object for the tip calculator.
 * @param method - The method of the tip calculator.
 * @param tipCalcResults - The tip calculation results.
 * @param site - The current site, providing settings.
 * @param posResults - The calculator's POS results, carrying integrations.
 * @param daySegments - The site's configured day segments at payment time.
 * @returns The payment set calculations object.
 */
const buildPaymentSetCalculations = (method: TipCalculatorMethod, tipCalcResults: ITipCalculatorResult[], site: ISite, posResults?: IPOSResults, daySegments?: IDaySegment[]): IPaymentSetCalculations => {
  const { businessDayStart, gracePeriodPolicy, timezone, tipCalculatorPercentFee: creditCardPercentFee } = site.settings;
  const paymentSetDaySegments: IPaymentSetDaySegment[] = (daySegments || []).map(({ name, start, end, endsNextDay }) => ({ name, start, end, endsNextDay: Boolean(endsNextDay) }));
  const settings: IPaymentSetCalculationsSettings = {
    gracePeriodPolicy,
    ...(posResults?.settings?.integrations && { integrations: posResults.settings.integrations }),
    ...(paymentSetDaySegments.length > 0 && { daySegments: paymentSetDaySegments }),
    ...(typeof creditCardPercentFee === 'number' && { creditCardPercentFee }),
    businessDayStart,
    timezone,
  };

  return {
    method: method as TipCalculatorMethod.SHARE,
    results: tipCalcResults.map((result) => ({
      ...result,
      segment: result.segmentId === DEFAULT_POOL_ID ? DEFAULT_POOL_NAME : result?.segment,
      creditCardPercentFee,
    })),
    settings,
  };
};

const sumSplitChecksReceived = (splitChecks?: ITipCalculatorResultSplitChecks): number => (splitChecks?.received || []).reduce((sum, rec) => sum + (rec.totals?.total || 0), 0);

/**
 * Returns the POS data rows for non-recipient contributors — those who collected gross > 0
 * from the POS but received nothing back (calculated.total + splitChecks.received === 0).
 * These rows surface on the "Other Contributors" tab.
 *
 * @param posData - Rows from the POS results, one per provider/employee
 * @param tipCalcResults - Per-segment calculator results (used to compute received totals)
 */
export const getNonRecipientContributors = (posData: IPOSData[], tipCalcResults: ITipCalculatorResult[]): IPOSData[] => {
  if (!posData?.length) return [];

  // Pre-compute the set of participants/employees that received any tips so the per-posData lookup is O(1).
  // Tip calculator output places job-title contributors under `result.participants` and matched
  // (or unmatched-but-known) individual employees under `result.employees`. Both can carry received totals.
  const receivedIds = new Set<string>();
  const receivedNames = new Set<string>();
  const trackReceived = (p: { id?: string; name?: string; calculated?: { total?: number }; splitChecks?: ITipCalculatorResultSplitChecks }) => {
    const received = (p.calculated?.total || 0) + sumSplitChecksReceived(p.splitChecks);
    if (received === 0) return;
    if (p.id) receivedIds.add(p.id);
    const normalizedName = p.name?.trim().toLowerCase();
    if (normalizedName) receivedNames.add(normalizedName);
  };
  (tipCalcResults || []).forEach((result) => {
    (result.participants || []).forEach(trackReceived);
    (result.employees || []).forEach(trackReceived);
  });

  return posData.filter((data) => {
    if (!data.provider?.name) return false;

    const collectedTotal = (data.segments || []).reduce((acc, segment) => acc + (segment.totals?.gross || 0), 0);
    if (collectedTotal <= 0) return false;

    const jobTitle = data.provider.schedules?.[0]?.jobTitle?.trim().toLowerCase();
    const hasReceived = receivedIds.has(data.provider.employeeId) || (!!data.employee?.id && receivedIds.has(data.employee.id)) || (!!jobTitle && receivedNames.has(jobTitle));
    return !hasReceived;
  });
};

export const calculateCreditCardFee = (creditCardAmount: number, feePercent: number): number => Math.floor(creditCardAmount * ((feePercent || 0) / 100));

export const getSortedUniqueSegments = (integrationData: IPOSResults, segments: IDaySegment[]): IPOSResultSegment[] => {
  const uniquePOSResultSegments: IPOSResultSegment[] = [];
  const posSegments: IPOSResultSegment[] = [...integrationData.data.flatMap((item) => item.segments.map((segment) => ({ id: segment.id, name: segment.name })))];

  const seenIds = new Set<string>();
  const filteredUniqueSegments = posSegments.filter((segment) => segment && !seenIds.has(segment?.id) && seenIds.add(segment?.id));

  filteredUniqueSegments.forEach((uniqueSegment) => {
    const segment = segments.find((s) => s.id === uniqueSegment.id);
    if (segment) uniquePOSResultSegments.push(segment);
    if (!segment) uniquePOSResultSegments.push(uniqueSegment);
  });

  // sort the results by start to make sure items are listed in the correct order.
  // For `default pool, we do not have a start & end date.  We always want it to show last.
  return uniquePOSResultSegments.sort((a: IPOSResultSegment, b: IPOSResultSegment) => {
    const start = parseInt(a.start?.split(':').join(''), 10) || 10_000;
    const end = parseInt(b.start?.split(':').join(''), 10) || 10_000;
    return start - end;
  });
};

interface ICheckLocation {
  segment?: IPOSSegment;
  ticket?: IPOSTicket;
}

/**
 * Resolves the segment and ticket a check points at.
 *
 * A check's `key` is its position in the POS tree (see `buildSortedChecksList`), so this is a
 * direct lookup rather than a search. The ids are then compared as a guard: if the tree has changed
 * shape since the check was built, the position now holds a different ticket and we return nothing
 * rather than writing to the wrong one.
 *
 * @param posData - The POS results to look in.
 * @param check - The check being acted on.
 * @returns The owning segment and ticket, or an empty object when the check cannot be resolved.
 */
export const findCheckLocation = (posData: IPOSResults, check: ICheck): ICheckLocation => {
  const [dataIndex, segmentIndex, ticketIndex] = check.key.split('.').map(Number);
  const segment = posData?.data[dataIndex]?.segments[segmentIndex];
  const ticket = segment?.tickets[ticketIndex];

  if (ticket?.ticketId !== check.ticketId || ticket.displayId !== check.displayId) return {};

  return { segment, ticket };
};

/**
 * Flattens POS results into the sorted check list for the Checks step.
 *
 * A POS `ticketId` is not unique, so each check carries a `key` instead: its position in
 * `posResults` (`"<data>.<segment>.<ticket>"`). That is how split, ignore, undo and cash edits find
 * their way back to the ticket. The sort runs after keys are assigned.
 */
export const buildSortedChecksList = (posResults: IPOSResults): ICheck[] =>
  posResults.data
    .flatMap((item, dataIndex) =>
      item.segments.flatMap((segment, segmentIndex) =>
        segment.tickets.map((ticket, ticketIndex) => ({
          ...ticket,
          key: `${dataIndex}.${segmentIndex}.${ticketIndex}`,
          segmentName: segment.name,
          employeeId: item.employee?.id || item.provider.employeeId,
          employeeName: item.employee?.name || item.provider.name || DEFAULT_POOL_UNKNOWN_JOB_TITLE,
        }))
      )
    )
    .sort(sortAlphaNumerically('displayId', 'ticketId'));

export const buildSortedParticipantList = (posResults: IPOSResults, participants: ICalculatorParticipant[] = []): ISplitParticipant[] => {
  const employees: ISplitParticipant[] = [];

  posResults?.data.forEach((item) => {
    if (item.employee) {
      employees.push({
        id: item.employee.id,
        name: item.employee.name,
        type: CalculatorParticipantType.JOB,
      });
    } else if (item.provider?.name && item.provider.name.trim().toLowerCase() !== DEFAULT_POOL_UNKNOWN_JOB_TITLE) {
      employees.push({
        id: item.provider.employeeId,
        name: item.provider.name,
        type: CalculatorParticipantType.JOB,
      });
    }
  });

  return [...employees, ...participants.map((participant) => ({ ...participant, id: participant.name.toLowerCase().replace(' ', '_') }))].sort(sortAlphaNumerically('name'));
};

/**
 * Calculates an even distribution of a gross amount among a list of ticket split shares.
 * The `gross` is distributed ensuring all cents are allocated as evenly as possible.
 *
 * @param {IPOSTicketSplitOverrideShare[]} shares - Array of share objects
 * @param {number} gross - The total gross amount to distribute.
 * @returns {IPOSTicketSplitOverrideShare[]} Updated shares with `appliedPercent` (initial even percentage),
 * `percent` (recalculated actual percentage), and `gross` (actual distributed amount).
 */
export const calculateEvenTicketSplitPercent = (shares: IPOSTicketSplitOverrideShare[], gross: number): IPOSTicketSplitOverrideShare[] => {
  const numberOfEmployees = shares.length;
  if (numberOfEmployees === 0) return [];

  const totalPercentage = 100;
  const basePercentage = Math.floor((totalPercentage * 100) / numberOfEmployees) / 100; // Base percentage with 2 decimals
  const evenPercentages = new Array(numberOfEmployees).fill(basePercentage);
  const grossDistribution = calculateDistribution(evenPercentages, gross);

  // recalculate percents so the total adds up to 100%
  const resultPercentages = calculatePercents(grossDistribution, gross);

  return shares.map((share, index) => ({
    ...share,
    appliedPercent: evenPercentages[index],
    percent: resultPercentages[index],
    gross: grossDistribution[index],
  }));
};

/**
 * Distributes a total amount among shares based on their percentages,
 * ensuring the entire total is allocated by handling remainders.
 *
 * It first calculates an initial distribution by applying each share's percentage to the total amount
 * and flooring the result. The remainder is then allocated by prioritizing shares with larger initial
 * fractional parts, followed by a round-robin distribution of cents until the `total` is fully allocated.
 *
 * @param {number[]} shares - An array of numbers representing the percentage share for each recipient (e.g., [25, 75] for 25% and 75%).
 * @param {number} total - The total amount to be distributed.
 * @returns {number[]} An array of numbers representing the actual amount distributed to each share.
 */
export const calculateDistribution = (shares: number[], total: number): number[] => {
  // Calculate the initial distribution and remove fractional parts
  const { distribution, fractionalParts, distributedTotal, indices } = shares.reduce(
    (acc, sharePercent, i) => {
      const amount = (sharePercent / 100) * total;
      const flooredAmount = Math.floor(amount);
      const decimalPart = amount - flooredAmount;

      acc.distributedTotal += flooredAmount;
      acc.distribution.push(flooredAmount);
      acc.fractionalParts.push(decimalPart);
      acc.indices.push(i);
      return acc;
    },
    { distributedTotal: 0, distribution: [], fractionalParts: [], indices: [] }
  );

  let remainder = total - distributedTotal;

  const sortedIndices = indices.sort((a, b) => fractionalParts[b] - fractionalParts[a]);

  // Distribute the remainder evenly prioritizing shares with larger initial fractional parts
  for (let i = 0; remainder > 0; i = (i + 1) % distribution.length) {
    const index = sortedIndices[i];
    distribution[index]++;
    remainder--;
  }
  return distribution;
};

const calculatePercents = (shares: number[], total: number): number[] => {
  // Calculate the initial distribution
  const percents = shares.map((share) => Math.floor(((parseFloat(share.toString()) * 100) / total) * 100) / 100);

  // Calculate the remainder
  const percentTotal = percents.reduce((sum, amount) => sum + amount, 0);
  const remainderPercentage = 100 - percentTotal;

  // Create an array of indices and sort them based on the initial percents (smallest first).
  // This prioritizes giving the remainder percents to the ones assigned less initially due to flooring.
  const sortedIndices = percents.map((_, i) => i).sort((a, b) => percents[a] - percents[b]);

  // Distribute the remainder
  for (let i = 0; i < Math.round(remainderPercentage * 100); i++) {
    const index = sortedIndices[i % shares.length];
    percents[index] += 0.01;
  }
  return percents.map((percent) => Math.round(percent * 100) / 100);
};

/**
 * Distributes a check's tip and auto-gratuity amounts based on share percentages.
 * If `calculateGross` is true, gross distribution is also recalculated based on the percentages.
 *
 * @param {IPOSTicketSplitOverrideShare[]} shares - Share objects defining participant portions.
 * @param {ICheck} check - Check object with tip and auto-gratuity totals.
 * @param {boolean} [calculateGross=false] - If true, forces recalculation of gross amounts per share.
 * @returns {IPOSTicketSplitOverrideShare[]} Updated shares with `percent`, `gross`, `tip`, and `autoGratuity`.
 */
export const calculateTicketSplitTotal = (shares: IPOSTicketSplitOverrideShare[], check: ICheck, calculateGross?: boolean): IPOSTicketSplitOverrideShare[] => {
  if (shares.length === 0) return [];

  // Get the first share to determine the distribution type
  const firstShare = shares[0];
  const hasTips = firstShare.sources.includes(RuleFundSource.TIPS);
  const hasAutoGratuities = firstShare.sources.includes(RuleFundSource.AUTO_GRATUITIES);

  const grossTotal = check.totals.gross;
  const netTipTotal = check.totals.tip.net;
  const netAutoGratuityTotal = check.totals.autoGratuity.net;

  let { grossDistribution, appliedPercents, percents } = shares.reduce(
    (acc, share) => {
      acc.grossDistribution.push(Number(share.gross));
      acc.appliedPercents.push(Number(share.appliedPercent) || 0);
      acc.percents.push(Number(share.percent) || 0);
      return acc;
    },
    { grossDistribution: [], appliedPercents: [], percents: [] }
  );

  // Percentages might not add up to 100% because of rounding issues, but we can recalculate percentages from entered amounts to ensure they add up to 100%
  const totalPercentage = Math.round(percents.reduce((sum, percent) => sum + percent, 0) * 100) / 100;
  if (totalPercentage !== 100) {
    percents = calculatePercents(
      grossDistribution,
      grossDistribution.reduce((sum, amount) => sum + amount, 0)
    );
    appliedPercents = percents;
  }

  if (calculateGross) {
    const currentGrossTotal = hasTips && hasAutoGratuities ? grossTotal : hasTips ? check.totals.tip.gross : check.totals.autoGratuity.gross;
    grossDistribution = calculateDistribution(appliedPercents, currentGrossTotal);
  }
  const tipDistribution = grossTotal === netTipTotal ? grossDistribution : calculateDistribution(appliedPercents, hasTips ? netTipTotal : 0);
  const autoGratuityDistribution = grossTotal === netAutoGratuityTotal ? grossDistribution : calculateDistribution(appliedPercents, hasAutoGratuities ? netAutoGratuityTotal : 0);

  return shares.map((share, idx) => ({
    ...share,
    percent: percents[idx],
    gross: grossDistribution[idx],
    tip: tipDistribution[idx],
    autoGratuity: autoGratuityDistribution[idx],
  }));
};

/**
 * Groups tip calculator warnings into capped and unallocated-funds buckets, tagging each with its segment.
 *
 * @param {ITipCalculatorResult[]} results - Tip calculator results.
 * @returns Pre-categorized warnings: `cappedWarnings` and `unallocatedFundsWarnings`.
 */
export const buildTipCalculatorWarnings = (results: ITipCalculatorResult[]) =>
  (results || []).reduce(
    (acc, result) => {
      result.warnings?.forEach((warning) => {
        const segmentWarning = { ...warning, segment: result.segment };
        switch (warning.type) {
          case TipCalculatorWarningType.CAPPED_SHARE:
          case TipCalculatorWarningType.CAPPED_REMAINDER:
            acc.cappedWarnings.push(segmentWarning);
            break;
          case TipCalculatorWarningType.UNALLOCATED_REMAINDER:
            acc.unallocatedFundsWarnings.push(segmentWarning);
            break;
          default:
            break;
        }
      });
      return acc;
    },
    { cappedWarnings: [], unallocatedFundsWarnings: [] }
  );

/**
 * Updates a segment's totals by adding or subtracting a ticket's totals.
 * This is used for handling ticket overrides like 'split' and 'ignore'.
 *
 * @param {IPOSSegment} segment - The original POS segment whose totals are being updated.
 * @param {IPOSTicket} ticket - The ticket being added or removed.
 * @param {'+' | '-'} operator - The operation to perform: '+' to add the ticket's totals, '-' to subtract.
 * @returns {IPOSSegmentTotals} The new segment totals after applying the ticket's values.
 */
export const updateSegmentTotalsWithTicket = (segment: IPOSSegment, ticket: IPOSTicket, operator: '+' | '-'): IPOSSegmentTotals => {
  if (ticket.totals.gross > 0) {
    const multiplier = operator === '+' ? 1 : -1;

    const calculateTotals = (segmentTotals: IPOSSegmentTotals, ticketTotals: IPOSTicketTotals, key: 'autoGratuity' | 'tip') => {
      const cash = Math.max(segmentTotals[key].cash + multiplier * ticketTotals[key].cash, 0);
      const creditCard = Math.max(segmentTotals[key].creditCard + multiplier * ticketTotals[key].creditCard, 0);
      const creditCardFee = Math.max(segmentTotals[key].creditCardFee + multiplier * ticketTotals[key].creditCardFee, 0);
      const gross = cash + creditCard;
      const net = gross - creditCardFee;
      return { cash, creditCard, creditCardFee, gross, net };
    };

    const newAutoGratuity = calculateTotals(segment.totals, ticket.totals, 'autoGratuity');
    const newTip = calculateTotals(segment.totals, ticket.totals, 'tip');

    return {
      ...segment.totals,
      autoGratuity: newAutoGratuity,
      tip: newTip,
      gross: newAutoGratuity.gross + newTip.gross,
      net: newAutoGratuity.net + newTip.net,
    };
  }

  return segment.totals;
};

/**
 * Applies (or removes) a ticket override on the ticket a check points at, keeping the owning
 * segment's totals in step. Mutates `posData`, so callers must pass a clone they own.
 *
 * Shared by the split, ignore and undo flows -- they differ only in the override being written.
 *
 * @param posData - The POS results to mutate.
 * @param check - The check being acted on.
 * @param override - The override to write, or `undefined` to remove an existing one.
 * @returns `true` when the override was applied, `false` when the check could not be resolved or
 * there was nothing to remove.
 */
export const applyTicketOverride = (posData: IPOSResults, check: ICheck, override: IPOSTicket['override']): boolean => {
  const { segment, ticket } = findCheckLocation(posData, check);
  const wasOverridden = !!ticket?.override;

  // Nothing to do when the check cannot be resolved, or when there is no override to remove
  if (!ticket || (!override && !wasOverridden)) return false;

  if (override) ticket.override = override;
  else delete ticket.override;

  const overrideAdded = !!override && !wasOverridden;
  const overrideRemoved = !override && wasOverridden;

  // A ticket belongs in its segment totals only while it carries no override: it leaves them when
  // an override is added and rejoins them when one is removed. Replacing an override with another
  // is neither, so a ticket can never be subtracted twice.
  if (overrideAdded || overrideRemoved) segment.totals = updateSegmentTotalsWithTicket(segment, ticket, overrideAdded ? '-' : '+');

  return true;
};

/**
 * Applies an edited cash tip amount to the ticket a check points at, moving the owning segment's
 * totals by the same delta. Mutates `posData`, so callers must pass a clone they own.
 *
 * @param posData - The POS results to mutate.
 * @param check - The check carrying the new cash tip amount.
 * @returns `true` when the edit was applied, `false` when the check could not be resolved.
 */
export const applyCashTipEdit = (posData: IPOSResults, check: ICheck): boolean => {
  const { segment, ticket } = findCheckLocation(posData, check);
  if (!ticket) return false;

  // Only the cash portion of the tip can be edited, so every affected total moves by that one delta.
  const cashDelta = check.totals.tip.cash - ticket.totals.tip.cash;

  segment.totals = {
    ...segment.totals,
    tip: {
      ...segment.totals.tip,
      gross: segment.totals.tip.gross + cashDelta,
      net: segment.totals.tip.net + cashDelta,
      cash: segment.totals.tip.cash + cashDelta,
    },
    gross: segment.totals.gross + cashDelta,
    net: segment.totals.net + cashDelta,
  };
  ticket.totals = check.totals;

  return true;
};

// Returns sales items grouped by category group and sorted by amount
export const buildSalesItems = (payout: ITipsBreakdown | ITipCalculatorResultEmployee, categoryGroups: ICategoryGroup[]): ITipsBreakdownLineItem[] => {
  const [groups, categories] = partition(payout.categories || [], (category) => category.type === ShareRuleType.CATEGORY_GROUP);

  const groupItems: ITipsBreakdownLineItem[] = groups
    ?.map((group) => {
      const salesGroup = categoryGroups?.find((g) => g.name.toLowerCase() === group.name.toLowerCase());
      return {
        label: toTitleCase(group.name),
        amount: group.total,
        subItems: categories
          .filter((category) => salesGroup?.categories.map((c) => c.toLowerCase()).includes(category.name.toLowerCase()))
          .map((category) => ({
            label: toTitleCase(category.name),
            amount: category.total,
          }))
          .sort((a, b) => b.amount - a.amount),
      };
    })
    .sort((a, b) => b.amount - a.amount);

  const categoryItems: ITipsBreakdownLineItem[] = categories
    .map((group) => ({
      label: toTitleCase(group.name),
      amount: group.total,
    }))
    .sort((a, b) => b.amount - a.amount);

  return [...groupItems, ...categoryItems];
};

// Returns check sales items grouped by category group and sorted by amount
export const buildCheckSalesItems = (categories: IPOSTicketCategory[], categoryGroups: ICategoryGroup[] = []): ITipsBreakdownLineItem[] => {
  // Group categories by name (case insensitive) and sum their totals
  const groupedCategories = categories.reduce(
    (acc, category) => {
      const normalizedName = category.name.toLowerCase();
      if (!acc[normalizedName]) {
        acc[normalizedName] = {
          name: category.name,
          total: 0,
        };
      }
      acc[normalizedName].total += category.total;
      return acc;
    },
    {} as Record<string, { name: string; total: number }>
  );

  // Convert grouped categories back to array format
  const uniqueCategories = Object.values(groupedCategories).map(({ name, total }) => ({
    name,
    total,
  }));

  // Use uniqueCategories instead of categories for the rest of the function
  const groupItems: ITipsBreakdownLineItem[] = categoryGroups
    .map((salesGroup) => {
      const groupItems = uniqueCategories
        .filter((category) => salesGroup?.categories.map((c) => c.toLowerCase()).includes(category.name.toLowerCase()))
        .map((category) => ({
          label: toTitleCase(category.name),
          amount: category.total,
        }))
        .sort((a, b) => b.amount - a.amount);

      return {
        label: toTitleCase(salesGroup.name),
        amount: groupItems.reduce((sum, item) => sum + item.amount, 0),
        subItems: groupItems,
      };
    })
    .sort((a, b) => b.amount - a.amount)
    .filter((item) => item.amount > 0);

  const categoryItems: ITipsBreakdownLineItem[] = uniqueCategories
    .map((group) => ({
      label: toTitleCase(group.name),
      amount: group.total,
    }))
    .sort((a, b) => b.amount - a.amount);

  return [...groupItems, ...categoryItems];
};

/**
 * Takes array of RuleFundSource (tips, auto_gratuities) and returns 'Tips & Autograts', 'Tips' or 'Autograts'
 *
 * @param sources array of RuleFundSource
 * @returns sources string
 */
export const getTipSources = (sources: RuleFundSource[]) => toTitleCase(orderBy(sources, String, 'desc').join(' & ').replace(RuleFundSource.AUTO_GRATUITIES, 'Autograts'));

interface IOverrideBreakdownData {
  splitChecks: ITipCalculatorResultSplitChecks;
  ignoredChecks: ITipCalculatorResultIgnoredCheck[];
}

/**
 * Takes array of ITipCalculatorResultEmployee and returns an object of IOverrideBreakdownData
 *
 * @param calculations array of ITipCalculatorResultEmployee
 * @returns an object of all split check data and ignored check data for an employee
 */
export const buildEmployeeOverrideTicketBreakdown = (calculations: ITipCalculatorResultEmployee[]): IOverrideBreakdownData =>
  calculations.reduce(
    (acc, calculation) => {
      if (calculation.splitChecks) {
        acc.splitChecks.received.push(...calculation.splitChecks.received);
        acc.splitChecks.contributions.push(...calculation.splitChecks.contributions);
      }

      if (calculation.ignoredChecks) acc.ignoredChecks.push(...calculation.ignoredChecks);

      return acc;
    },
    {
      splitChecks: { received: [], contributions: [] },
      ignoredChecks: [],
    }
  );

/**
 * Takes array of ITipCalculatorResultParticipant and returns an object of ITipCalculatorResultSplitChecks
 *
 * @param calculations array of ITipCalculatorResultParticipant
 * @returns an object of all split check data a participant
 */
export const buildParticipantSplitCheckBreakdown = (calculations: ITipCalculatorResultParticipant[]): ITipCalculatorResultSplitChecks =>
  calculations.reduce(
    (acc, calculation) => {
      if (calculation.splitChecks) {
        acc.received.push(...calculation.splitChecks.received);
        acc.contributions.push(...calculation.splitChecks.contributions);
      }

      return acc;
    },
    { received: [], contributions: [] }
  );

/**
 * Builds a list of check change log entries based on modifications to a check, such as edited cash tips or ignore actions.
 *
 * @param action - The type of change (e.g., CASH_EDITED, IGNORE, IGNORE_UNDO).
 * @param changeLogEntries - The existing list of change log entries.
 * @param posData - The POS results containing segment and ticket information.
 * @param check - The check that was modified.
 * @returns A new array of change log entries with applicable modifications applied.
 */
export const buildCheckChangeLogEntries = (action: ChangeLogAction, changeLogEntries: ITipCalculatorChangeLogEntry[], posData: IPOSResults, check: ICheck): ITipCalculatorChangeLogEntry[] => {
  const isSplit = [ChangeLogAction.SPLIT, ChangeLogAction.UNDO_OVERRIDE].includes(action);
  const isIgnore = [ChangeLogAction.IGNORE, ChangeLogAction.UNDO_OVERRIDE].includes(action);
  const isCashEdited = action === ChangeLogAction.CASH_EDITED;

  if (!isSplit && !isIgnore && !isCashEdited) return [...changeLogEntries];

  // `ticketId` is not unique, so we need to check for `displayId` as well.
  const isSameCheck = (entry: ITipCalculatorChangeLogEntry) => {
    const details = entry.details as CheckModifiedDetails;
    return details?.id === check.ticketId && details?.displayId === check.displayId;
  };

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isRelevantAction =
      (isCashEdited && entry.action === ChangeLogAction.CASH_EDITED) ||
      (isIgnore && [ChangeLogAction.IGNORE, ChangeLogAction.UNDO_OVERRIDE].includes(entry.action)) ||
      (isSplit && [ChangeLogAction.SPLIT, ChangeLogAction.UNDO_OVERRIDE].includes(entry.action));
    return !(entry.entity === ChangeLogEntity.CHECK && isRelevantAction && isSameCheck(entry));
  });

  // If UNDO_OVERRIDE, just return filtered list (don't add new)
  if (action === ChangeLogAction.UNDO_OVERRIDE) return updatedEntries;

  // Find POS segment and ticket
  const { segment: posDataSegment, ticket: posTicket } = findCheckLocation(posData, check);
  if (!posTicket) return updatedEntries;

  // For CASH_EDITED: check if there's an existing entry with matching from amount
  let existingEntry: ITipCalculatorChangeLogEntry | undefined;
  if (isCashEdited) {
    // Find existing entry with matching from amount
    existingEntry = changeLogEntries.find((entry) => entry.entity === ChangeLogEntity.CHECK && entry.action === ChangeLogAction.CASH_EDITED && isSameCheck(entry));

    if ((existingEntry?.details as CheckModifiedDetails)?.from === check.totals.tip.cash) return updatedEntries;
  }

  // Base change log entry shared across IGNORE, SPLIT and CASH_EDITED
  const baseEntry: ITipCalculatorChangeLogEntry = {
    entity: ChangeLogEntity.CHECK,
    action,
    participant: {
      name: check.employeeName,
      type: ChangeLogParticipantType.JOB,
      job: posDataSegment.job.title,
    },
    segment: posDataSegment.name,
    details: {
      id: check.ticketId,
      displayId: check.displayId,
      ...(isCashEdited && {
        from: (existingEntry?.details as CheckModifiedDetails)?.from ?? posTicket.totals.tip.cash,
        to: check.totals.tip.cash,
      }),
    } as CheckModifiedDetails,
    timestamp: dayjs().utc().toISOString(),
  };

  return [...updatedEntries, baseEntry];
};

interface ILaborChangeLogEmployee {
  id: string;
  name: string;
  segmentName?: string;
  jobTitle?: string;
  from?: number;
  to?: number;
}

/**
 * Builds and manages change log entries for labor-related modifications.
 * This function handles three types of labor changes:
 * - Removal of labor data
 * - Removal of labor segments
 * - Editing of segment durations
 *
 * @param {ChangeLogAction} action - The type of action being performed (REMOVED, SEGMENT_REMOVED, SEGMENT_DURATION_EDITED)
 * @param {ITipCalculatorChangeLogEntry[]} changeLogEntries - The existing array of change log entries
 * @param {ILaborChangeLogEmployee} employee - The employee data being modified
 * @param {string} employee.id - The unique identifier of the employee
 * @param {string} employee.name - The name of the employee
 * @param {string} [employee.segmentName] - Optional name of the segment the employee belongs to
 * @param {string} [employee.jobTitle] - Optional job title of the employee
 * @param {number} [employee.from] - Optional start time for segment duration edits
 * @param {number} [employee.to] - Optional end time for segment duration edits
 * @returns {ITipCalculatorChangeLogEntry[]} Updated array of change log entries
 */
export const buildLaborChangeLogEntries = (action: ChangeLogAction, changeLogEntries: ITipCalculatorChangeLogEntry[], employee: ILaborChangeLogEmployee): ITipCalculatorChangeLogEntry[] => {
  const isRemoved = [ChangeLogAction.REMOVED].includes(action);
  const isRemovedSegment = [ChangeLogAction.SEGMENT_REMOVED].includes(action);
  const isSegmentDurationEdited = [ChangeLogAction.SEGMENT_DURATION_EDITED].includes(action);

  if (!isRemoved && !isRemovedSegment && !isSegmentDurationEdited) return [...changeLogEntries];

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isSameEmployee = (entry.details as LaborDataDetails)?.id === employee.id;
    const isRelevantAction =
      (isSegmentDurationEdited &&
        entry.action === ChangeLogAction.SEGMENT_DURATION_EDITED &&
        entry.segment === employee.segmentName &&
        entry.participant.type === ChangeLogParticipantType.JOB &&
        entry.participant.job === employee.jobTitle) ||
      (isRemoved && [ChangeLogAction.REMOVED].includes(entry.action)) ||
      (isRemovedSegment && [ChangeLogAction.SEGMENT_REMOVED].includes(entry.action));
    return !(entry.entity === ChangeLogEntity.LABOR_DATA && isRelevantAction && isSameEmployee);
  });

  // For SEGMENT_DURATION_EDITED: check if there's an existing entry with matching from value
  let existingEntry: ITipCalculatorChangeLogEntry | undefined;
  if (isSegmentDurationEdited) {
    existingEntry = changeLogEntries.find(
      (entry) =>
        entry.entity === ChangeLogEntity.LABOR_DATA &&
        entry.action === ChangeLogAction.SEGMENT_DURATION_EDITED &&
        entry.segment === employee.segmentName &&
        entry.participant.type === ChangeLogParticipantType.JOB &&
        entry.participant.job === employee.jobTitle &&
        (entry.details as LaborDataDetails)?.id === employee.id
    );

    if ((existingEntry?.details as LaborDataDetails)?.from === employee.to) return updatedEntries;
  }

  // Base change log entry
  const entry: ITipCalculatorChangeLogEntry = {
    entity: ChangeLogEntity.LABOR_DATA,
    action,
    participant: {
      name: employee.name,
      type: employee.jobTitle ? ChangeLogParticipantType.JOB : ChangeLogParticipantType.EMPLOYEE,
      ...(employee.jobTitle && { job: employee.jobTitle }),
    },
    details: {
      id: employee.id,
    } as LaborDataDetails,
    ...(employee.segmentName && { segment: employee.segmentName }),
    timestamp: dayjs().utc().toISOString(),
  };

  if (isSegmentDurationEdited) {
    entry.details.from = existingEntry?.details.from ?? employee.from;
    entry.details.to = employee.to;

    // Don't add entry if from & to are equal
    if (entry.details.from === entry.details.to) return updatedEntries;
  }

  return [...updatedEntries, entry];
};

interface ITipDataAmountTotals {
  id: string;
  name: string;
  segmentName: string;
  jobTitle?: string;
  source: SourceType;
  type: SourcePaymentType;
  value: number;
  totals: IPOSSegmentTotals;
}

export const buildTipDataChangeLogEntries = (tipData: ITipDataAmountTotals, changeLogEntries: ITipCalculatorChangeLogEntry[]): ITipCalculatorChangeLogEntry[] => {
  // Set action
  let action: ChangeLogAction;
  switch (tipData.type) {
    case SourcePaymentType.CREDIT:
      action = tipData.source === SourceType.TIP ? ChangeLogAction.CREDIT_CARD_TIPS_EDITED : ChangeLogAction.CREDIT_AUTOGRATS_EDITED;
      break;
    case SourcePaymentType.CASH:
      action = tipData.source === SourceType.TIP ? ChangeLogAction.CASH_TIPS_EDITED : ChangeLogAction.CASH_AUTOGRATS_EDITED;
      break;
    default:
      return changeLogEntries;
  }

  const changeLogEntry = {
    entity: ChangeLogEntity.TIP_DATA,
    action,
    participant: { name: tipData.name, type: ChangeLogParticipantType.JOB, job: tipData.jobTitle },
    details: { id: tipData.id, from: tipData.totals[tipData.source][tipData.type], to: tipData.value },
    segment: tipData.segmentName,
    timestamp: dayjs().utc().toISOString(),
  };

  const existingEntry = changeLogEntries.find(
    (entry) =>
      entry.entity === ChangeLogEntity.TIP_DATA &&
      entry.action === changeLogEntry.action &&
      entry.segment === changeLogEntry.segment &&
      entry.participant.type === ChangeLogParticipantType.JOB &&
      entry.participant.job === changeLogEntry.participant.job &&
      entry.details.id === changeLogEntry.details.id
  );

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isSameEmployee = (entry.details as TipEditedDetails)?.id === changeLogEntry.details.id;
    const isRelevantAction = entry.action === changeLogEntry.action && entry.segment === changeLogEntry.segment && entry.participant.type === ChangeLogParticipantType.JOB && entry.participant.job === changeLogEntry.participant.job;

    return !(entry.entity === ChangeLogEntity.TIP_DATA && isRelevantAction && isSameEmployee);
  });

  // If the existing entry has the same from and to values, don't add a new entry
  if ((existingEntry?.details.from ?? changeLogEntry.details.from) === changeLogEntry.details.to) return updatedEntries;

  // Build change log entry
  return [...updatedEntries, { ...changeLogEntry, details: { ...changeLogEntry.details, from: existingEntry?.details.from ?? changeLogEntry.details.from } }];
};

interface IHoldbackChangeLogEntry {
  payee: ITipCalculatorPayee;
  previousHoldback: number;
  holdback: number;
}

export const buildHoldbackChangeLogEntries = (holdbackEntry: IHoldbackChangeLogEntry, changeLogEntries: ITipCalculatorChangeLogEntry[]): ITipCalculatorChangeLogEntry[] => {
  const changeLogEntry = {
    entity: ChangeLogEntity.PAYMENT,
    action: ChangeLogAction.HOLDBACK_EDITED,
    participant: { name: holdbackEntry.payee.employee?.name || holdbackEntry.payee.provider?.name, type: ChangeLogParticipantType.EMPLOYEE },
    details: { id: holdbackEntry.payee.id, from: holdbackEntry.previousHoldback, to: holdbackEntry.holdback },
    timestamp: dayjs().utc().toISOString(),
  };

  const existingEntry = changeLogEntries.find(
    (entry) => entry.entity === ChangeLogEntity.PAYMENT && entry.action === ChangeLogAction.HOLDBACK_EDITED && entry.participant.type === ChangeLogParticipantType.EMPLOYEE && entry.details.id === changeLogEntry.details.id
  );

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isSameEmployee = (entry.details as HoldbackEditedDetails)?.id === changeLogEntry.details.id;
    const isRelevantAction = entry.action === changeLogEntry.action && entry.participant.type === ChangeLogParticipantType.EMPLOYEE;

    return !(entry.entity === ChangeLogEntity.PAYMENT && isRelevantAction && isSameEmployee);
  });

  // If the existing entry has the same from and to values, don't add a new entry
  if ((existingEntry?.details.from ?? changeLogEntry.details.from) === changeLogEntry.details.to) return updatedEntries;

  // Build change log entry
  return [...updatedEntries, { ...changeLogEntry, details: { ...changeLogEntry.details, from: existingEntry?.details.from ?? changeLogEntry.details.from } }];
};

interface ICashPayoutChangeLogEntry {
  payee: ITipCalculatorPayee;
  value: number;
}

export const buildCashPayoutEnteredChangeLogEntries = (cashPayoutEntry: ICashPayoutChangeLogEntry, changeLogEntries: ITipCalculatorChangeLogEntry[]): ITipCalculatorChangeLogEntry[] => {
  const changeLogEntry = {
    entity: ChangeLogEntity.PAYMENT,
    action: ChangeLogAction.CASH_PAYOUT_ENTERED,
    participant: { name: cashPayoutEntry.payee.employee?.name || cashPayoutEntry.payee.provider.name, type: ChangeLogParticipantType.EMPLOYEE },
    details: { id: cashPayoutEntry.payee.id, from: 0, to: cashPayoutEntry.value },
    timestamp: dayjs().utc().toISOString(),
  };

  const existingEntry = changeLogEntries.find(
    (entry) => entry.entity === ChangeLogEntity.PAYMENT && entry.action === ChangeLogAction.CASH_PAYOUT_ENTERED && entry.participant.type === ChangeLogParticipantType.EMPLOYEE && entry.details.id === changeLogEntry.details.id
  );

  // If the existing entry has the same to value, don't add a new entry
  if (existingEntry && existingEntry.details?.to === cashPayoutEntry.value) return changeLogEntries;

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isSameEmployee = (entry.details as CashPayoutDetails)?.id === changeLogEntry.details.id;
    const isRelevantAction = entry.action === changeLogEntry.action && entry.participant.type === ChangeLogParticipantType.EMPLOYEE;

    return !(entry.entity === ChangeLogEntity.PAYMENT && isRelevantAction && isSameEmployee);
  });

  // If the entry to value is 0, do not add a new entry.
  if (changeLogEntry.details.to === 0) return updatedEntries;

  // If the existing entry from and to value are the same as the new entry, do not add a new entry.
  if (existingEntry && existingEntry.details.from === changeLogEntry.details.from && existingEntry.details.to === changeLogEntry.details.to) return updatedEntries;

  // Build change log entry
  return [...updatedEntries, { ...changeLogEntry, details: { ...changeLogEntry.details, from: existingEntry?.details.from ?? changeLogEntry.details.from } }];
};

export interface IChangeLogTableRow {
  number: number;
  category: string;
  event: string;
  item: { name: string; segment?: string; job?: string; check?: string };
  details?: { from: string; to: string };
}

/**
 * Transforms an array of change log entries into formatted table rows for display.
 * Handles various types of changes including labor data, tip data, and check modifications.
 *
 * @param {ITipCalculatorChangeLogEntry[]} entries - Array of change log entries to be formatted
 * @returns {IChangeLogTableRow[]} Array of formatted table rows containing:
 *   - number: Sequential row number
 *   - category: Formatted entity type (Labor, Tip Data, Checks)
 *   - event: Formatted action type (Cash Tips Edited, Hours Edited, Split, etc.)
 *   - item: Employee name with segment and job, or with the check number for check changes
 *   - details: Optional object containing formatted 'from' and 'to' values for changes that involve modifications
 */
export const buildChangeLogTableRows = (entries: ITipCalculatorChangeLogEntry[]): IChangeLogTableRow[] => {
  const formatEntity = (entity: ChangeLogEntity) => {
    switch (entity) {
      case ChangeLogEntity.LABOR_DATA:
        return 'Labor';
      case ChangeLogEntity.TIP_DATA:
        return 'Tip Data';
      case ChangeLogEntity.CHECK:
        return 'Checks';
      case ChangeLogEntity.PAYMENT:
        return 'Payments';
      default:
        return entity;
    }
  };

  const formatAction = (action: ChangeLogAction) => {
    switch (action) {
      case ChangeLogAction.CASH_EDITED:
        return 'Cash Tips Edited';
      case ChangeLogAction.IGNORE:
        return 'Ignored';
      case ChangeLogAction.SPLIT:
        return 'Split';
      case ChangeLogAction.SEGMENT_DURATION_EDITED:
        return 'Hours Edited';
      case ChangeLogAction.REMOVED:
        return 'Employee Removed';
      case ChangeLogAction.SEGMENT_REMOVED:
        return 'Job Removed';
      case ChangeLogAction.CREDIT_CARD_TIPS_EDITED:
        return 'CC Tips Edited';
      case ChangeLogAction.CREDIT_AUTOGRATS_EDITED:
        return 'CC Autograts Edited';
      case ChangeLogAction.CASH_AUTOGRATS_EDITED:
        return 'Cash Autograts Edited';
      case ChangeLogAction.CASH_TIPS_EDITED:
        return 'Cash Tips Edited';
      case ChangeLogAction.HOLDBACK_EDITED:
        return 'Holdback Edited';
      case ChangeLogAction.CASH_PAYOUT_ENTERED:
        return 'Cash Payout Entered';
      default:
        return action;
    }
  };

  const formatDetails = (entry: ITipCalculatorChangeLogEntry) => {
    switch (entry.action) {
      case ChangeLogAction.CASH_EDITED:
      case ChangeLogAction.CREDIT_CARD_TIPS_EDITED:
      case ChangeLogAction.CREDIT_AUTOGRATS_EDITED:
      case ChangeLogAction.CASH_AUTOGRATS_EDITED:
      case ChangeLogAction.CASH_TIPS_EDITED:
      case ChangeLogAction.HOLDBACK_EDITED:
      case ChangeLogAction.CASH_PAYOUT_ENTERED:
        return { from: centsToDollarsDisplay(entry.details.from), to: centsToDollarsDisplay(entry.details.to) };
      case ChangeLogAction.SEGMENT_DURATION_EDITED:
        return { from: new Duration(entry.details.from).toString(), to: new Duration(entry.details.to).toString() };
      case ChangeLogAction.SPLIT:
      case ChangeLogAction.IGNORE:
        return undefined;
      default:
        return undefined;
    }
  };

  const formatItem = (entry: ITipCalculatorChangeLogEntry) => {
    if (entry.entity === ChangeLogEntity.CHECK) return { name: entry.participant?.name || '', check: `#${(entry.details as CheckModifiedDetails).displayId}` };
    return { name: entry.participant?.name || '', segment: entry.segment, job: entry.participant?.job };
  };

  return entries.map((entry, index) => ({
    number: index + 1,
    category: formatEntity(entry.entity),
    event: formatAction(entry.action),
    item: formatItem(entry),
    details: formatDetails(entry),
  }));
};

interface IBuildPaymentSetRequestParams {
  method: TipCalculatorMethod;
  tipCalcResults: ITipCalculatorResult[];
  changeLog: ITipCalculatorChangeLogEntry[];
  description: string;
  paymentDate: dayjs.Dayjs | string;
  payees: ITipCalculatorPayee[];
  site: ISite;
  posResults?: IPOSResults;
  withholdings?: IPaymentWithholdings;
  daySegments?: IDaySegment[];
}

/**
 * Builds the payment set request object for payPaymentSetRequest.
 * @param params - Object containing all required fields for the payment set request
 * @returns The payment set request object
 */
export const buildPaymentSetRequest = ({ method, tipCalcResults, changeLog, description, paymentDate, payees, site, posResults, withholdings, daySegments }: IBuildPaymentSetRequestParams): ICreatePaymentPayload => ({
  calculations: buildPaymentSetCalculations(method, tipCalcResults, site, posResults, daySegments),
  changeLog,
  description: description || '',
  paymentDate: dayjs(paymentDate).format('YYYY-MM-DD'),
  refId: dayjs(paymentDate).format('YYYY-MM-DD'),
  refType: PayoutRefType.CALCULATOR,
  payouts: buildPaymentPayouts(payees),
  withholdings,
  siteId: site.id,
  workflow: PaymentWorkflowTypes.CALCULATOR,
});

/**
 * Validates a tip calculator payee and returns the payee with any validation errors attached.
 * This function performs multiple validation checks including auto clockout status, user setup completion,
 * payment amounts, and role-based permissions.
 *
 * @param {ITipCalculatorPayee} payee - The payee object to validate
 * @param {number} maxTipAmount - The maximum allowed tip amount for disbursements
 * @param {ISiteRolePolicy} rolePolicy - The site's role policy containing permission rules
 * @param {ICurrentEmployee} currentEmployee - The current employee performing the validation
 * @returns {ITipCalculatorPayee} The payee object with an errors array containing any validation errors found
 */
export const buildTipCalculatorPayeeErrors = (payee: ITipCalculatorPayee, maxTipAmount: number, rolePolicy: ISiteRolePolicy, currentEmployee: ICurrentEmployee): ITipCalculatorPayee => {
  const errors: ITipCalculatorPayeeError[] = [];

  // Auto Clockout
  // If the payee has an auto clockout, add an error
  const autoClockOut = payee.provider?.schedules.some((item) => item.clockedOut === false);
  if (autoClockOut) {
    errors.push({ type: TipCalculatorPayeeErrorType.AUTO_CLOCKOUT, preventSubmit: false });
  }

  // User Setup Not Complete
  // If the payee has no employee, add an error
  if (!payee.employee) {
    errors.push({ type: TipCalculatorPayeeErrorType.USER_SETUP_NOT_COMPLETE, preventSubmit: true });
  } else {
    const netPayment = payee.employee.disbursementMethod === DisbursementMethod.INSTANT ? calculateInstant(payee) : calculatePayroll(payee);

    // Negative Net Payment or holdback is taking the entire payment
    if (netPayment < 0 || (netPayment === 0 && payee.holdbackAmount > 0)) {
      errors.push({ type: TipCalculatorPayeeErrorType.NEGATIVE_NET_PAYMENT, preventSubmit: true });
    }

    // Max Payment Exceeded
    // If the payee's net payout is greater than the max tip amount, add an error
    if (payee.net > maxTipAmount) {
      errors.push({ type: TipCalculatorPayeeErrorType.MAX_PAYMENT_EXCEEDED, preventSubmit: true });
    }

    // Ineligible Self Pay & Ineligible Payable
    const currentPolicy = getRolePolicy(rolePolicy, payee.employee?.type, payee.employee?.role);
    if (currentPolicy) {
      if (payee.employee?.id === currentEmployee.id && !currentPolicy.paySelf) {
        errors.push({ type: TipCalculatorPayeeErrorType.INELIGIBLE_SELF_PAY, preventSubmit: true });
      } else if (!currentPolicy.payable) {
        errors.push({ type: TipCalculatorPayeeErrorType.INELIGIBLE_PAYABLE, preventSubmit: true });
      }
    }
  }

  return { ...payee, errors };
};

/**
 * Groups and aggregates payout data into tip calculator payee objects.
 *
 * This function takes a payment set with an array of individual payouts and consolidates them by employee ID,
 * combining multiple payouts for the same employee into a single payee object. It handles
 * instant, payroll and cash payouts, ensuring proper aggregation of financial amounts.
 *
 * @param {IPaymentSet} paymentSet - The payment set object containing payouts and withholdings.
 * @returns {ITipCalculatorPayee[]} Array of consolidated payee objects, sorted alphabetically by name
 */
export const buildGroupedTipCalculatorPayeesFromPayouts = (paymentSet?: IPaymentSet) => {
  const payeeMap = new Map<string, ITipCalculatorPayee>();

  paymentSet?.payouts.forEach((payout) => {
    const employeeId = payout.employee.id;
    const isCashPayout = payout.disbursementMethod === DisbursementMethod.CASH;
    const isInstantPayout = payout.disbursementMethod === DisbursementMethod.INSTANT;
    const withholdingAmount = payout.withholdings?.employee.amount || 0;

    if (payeeMap.has(employeeId)) {
      // Update existing payee
      const existingPayee = payeeMap.get(employeeId)!;
      existingPayee.gross += payout.gross;
      existingPayee.net += payout.net;
      existingPayee.autoGratuity += payout.autoGratuity;
      existingPayee.holdbackAmount += payout.holdback;
      existingPayee.withholdingAmount += withholdingAmount;
      if (isCashPayout) {
        existingPayee.cash += payout.net;
      } else if (isInstantPayout) {
        // if one of the payouts is instant, then the employee disbursementMethod has to be instant
        existingPayee.employee.disbursementMethod = DisbursementMethod.INSTANT;
      }
    } else {
      // Create new payee
      const newPayee: ITipCalculatorPayee = {
        id: employeeId,
        name: payout.employee.name,
        employee: {
          id: payout.employee.id,
          name: payout.employee.name,
          firstName: payout.employee.firstName,
          lastName: payout.employee.lastName,
          job: { title: payout.job.title },
          disbursementMethod: payout.disbursementMethod,
          type: EmployeeType.USER, // backend does not return this field, setting to USER
          role: SiteRole.EMPLOYEE, // backend does not return this field, setting to EMPLOYEE
        },
        calculations: [],
        gross: payout.gross,
        net: payout.net,
        autoGratuity: payout.autoGratuity,
        holdbackAmount: payout.holdback,
        cash: isCashPayout ? payout.net : 0,
        withholdingAmount,
        note: payout.notes,
      };
      payeeMap.set(employeeId, newPayee);
    }
  });

  return Array.from(payeeMap.values()).sort(sortAlphaNumerically('name'));
};

/**
 * Updates a list of tip calculator payees, POS results, and tip calculation results
 * when a POS provider is linked to an employee.
 *
 * @param {IEmployee} employee - The employee object that was linked.
 * @param {string} siteId - The ID of the site where the linking occurred.
 * @param {ITipCalculatorPayee[]} payees - The current array of tip calculator payees.
 * @param {IPOSResults} posResults - The current POS results.
 * @param {ITipCalculatorResult[]} tipCalcResults - The current tip calculation results.
 * @returns {{ updatedPayees: ITipCalculatorPayee[]; updatedPosResults: IPOSResults; updatedTipCalcResults: ITipCalculatorResult[] }} An object containing the updated payees, POS results, and tip calculation results.
 */
export const updatePayeeWithNewEmployee = (employee: IEmployee, site: ISite, payees: ITipCalculatorPayee[], posResults: IPOSResults, tipCalcResults: ITipCalculatorResult[], payrollTips?: IEmployeePayrollTip[]) => {
  const employeeSite = employee.sites.find((s) => s.id === site?.id);
  if (!employeeSite) throw new Error(`Cannot connect employee ${employee.id}: it has no role on site ${site?.id}`);

  const payeeToUpdate = payees.find((payee) => (employee.integrations || []).some((i) => i.id === payee.provider?.employeeId));
  if (!payeeToUpdate) throw new Error(`Cannot connect employee ${employee.id}: no payee matches its integrations`);

  const providerEmployeeId = payeeToUpdate.provider.employeeId;

  const newEmployee: IPayeeEmployee = {
    id: employee.id,
    name: employee.name,
    firstName: employee.firstName,
    lastName: employee.lastName,
    employeeId: employeeSite.employeeId,
    job: { title: employeeSite.job.title },
    role: employeeSite.role,
    type: employee.type,
    disbursementMethod: employeeSite.disbursementMethod,
    withholdings: employeeSite.withholdings,
  };

  const currentPayroll = payrollTips?.find((payroll) => payroll.employee.id === newEmployee.id)?.payroll.total || 0;

  // only update payee that was linked to new employee
  const updatedPayees = payees.map((payee) =>
    payeeToUpdate.id === payee.id
      ? {
          ...payee,
          name: newEmployee.name,
          employee: newEmployee,
          ...calculatePayeeTotals(payee.gross, site, newEmployee.disbursementMethod, newEmployee.withholdings, currentPayroll),
          cash: payee.cash,
        }
      : payee
  );

  // update the employee id on the calculated results
  const updatedTipCalcResults = tipCalcResults.map((result) => ({
    ...result,
    employees: result.employees.map((emp) => (emp.id === providerEmployeeId ? { ...emp, id: employee.id } : emp)),
  }));

  // update labor & ticket data with new employee data
  const updatedPosResults = {
    ...posResults,
    data: posResults.data.map((data) => ({
      ...data,
      employee: !data.employee && data.provider.employeeId === providerEmployeeId ? newEmployee : data.employee,
      segments: data.segments.map((segment) => ({
        ...segment,
        tickets: segment.tickets.map((ticket) => ({
          ...ticket,
          ...(ticket.override?.type === TicketOverrideType.SPLIT && {
            override: {
              ...ticket.override,
              shares: ticket.override.shares.map((share) => ({
                ...share,
                participant: share.participant.id === providerEmployeeId ? { ...share.participant, id: newEmployee.id, name: newEmployee.name } : share.participant,
              })),
            },
          }),
        })),
      })),
    })),
  };

  return { updatedPayees, updatedPosResults, updatedTipCalcResults };
};

/**
 * Checks whether the POS data belongs to a contributor whose owning rule has `isVirtual === true`.
 *
 * The Tip Data tab uses this to render a "Virtual" tag next to virtual contributors.
 */
export const isVirtualRuleContributor = (posData: IPOSData, calcRules: ICalculatorRules): boolean => {
  // Get virtual rules
  const virtualRules = (calcRules?.rules || []).filter((rule) => rule.isVirtual && !rule.default);
  if (virtualRules.length === 0) return false;

  // The provider is virtual if it is covered by a virtual owner rule (virtualRules are already filtered to isVirtual)
  if (getOwnerRule(virtualRules, posData.provider.employeeId)) return true;

  const jobTitle = posData.provider?.schedules?.[0]?.jobTitle?.toLowerCase();
  if (!jobTitle) return false;

  return virtualRules.some((rule) => rule.contributor?.name?.trim().toLowerCase() === jobTitle);
};

/**
 * Builds the list of payees rendered on the Labor Data screen from POS data and calculator rules.
 *
 * Excludes contributors that should not appear on the screen:
 * - virtual rule contributors (e.g., shared POS terminals) — we typically do not have labor data for them and labor edits would not affect tip calculations
 * - owner rule contributors (EMPLOYEE-typed contributors) — owner rules do not require the employee to have any labor
 *
 * For the remaining contributors, builds one labor entry per segment that is among the selected payment segments and whose job title
 * is rule-eligible (a contributing or benefitting job title), sorted by segment start time.
 * Payees without any labor entries are omitted, and the final list is sorted alphabetically by name.
 *
 * @param {IPOSResults} posData - The POS data to build payees from.
 * @param {IRuleEligibleJobTitles} ruleEligibleJobTitles - Precomputed rule lookups (see {@link getRuleEligibleJobTitles}).
 * @param {IDaySegment[]} paymentSetSegments - The day segments selected for payment.
 * @returns {ILaborDataPayee[]} The payees to render on the Labor Data screen.
 */
export const buildLaborDataPayees = (posData: IPOSResults, ruleEligibleJobTitles: IRuleEligibleJobTitles, paymentSetSegments: IDaySegment[]): ILaborDataPayee[] => {
  const payees: ILaborDataPayee[] = [];

  if (!paymentSetSegments.length) return payees;

  const { contributingJobTitles, benefittingJobTitles, ownerRuleProviderIds, virtualContributorJobTitles } = ruleEligibleJobTitles;

  // Index the payment segments by id for faster lookup
  const paymentSegmentsById = new Map(paymentSetSegments.map((segment) => [segment.id, segment]));

  // Participant ids that receive a share from any split-check override anywhere in the POS data.
  const splitCheckShareParticipantIds = new Set(
    posData.data
      .flatMap((item) => item.segments)
      .flatMap((segment) => segment.tickets)
      .flatMap((ticket) => (ticket.override?.type === TicketOverrideType.SPLIT ? ticket.override.shares.map((share) => share.participant?.id) : []))
      .filter(Boolean)
  );

  posData.data.forEach((data, idx) => {
    const laborData: IEmployeeLaborJobState[] = [];

    // skip owner rule contributors: owner rules do not require the employee to have labor at all, so they should not appear on the Labor Data screen.
    if (ownerRuleProviderIds.has(data.provider.employeeId)) return;

    // skip virtual rule contributors (e.g., shared POS terminals): we typically do not have labor data for them and labor edits would not affect tip calculations
    const providerJobTitle = data.provider?.schedules?.[0]?.jobTitle?.toLowerCase().trim();
    if (providerJobTitle && virtualContributorJobTitles.has(providerJobTitle)) return;

    const employeeId = data.employee?.id || data.provider.employeeId;

    // build labor data from segments
    data.segments?.forEach((seg) => {
      const daySegment = paymentSegmentsById.get(seg.id);
      if (daySegment) {
        const segmentJobTitle = normalizeJobTitle(seg.job.title);
        if (contributingJobTitles.has(segmentJobTitle) || benefittingJobTitles.has(segmentJobTitle)) {
          laborData.push({
            daySegment,
            jobTitle: seg.job,
            duration: seg.totals.totalMinWorked,
            total: seg.totals.tip.net,
            touched: false,
            error: undefined,
            id: `labor-${employeeId}-${seg.id}-${segmentJobTitle}`,
          });
        }
      }
    });

    // payees without any labor entries are not shown — exit early before computing the rest
    if (!laborData.length) return;

    // sort once here so validateJobData and EmployeeListItem share consistent ordering
    laborData.sort((a, b) => (a.daySegment?.start ?? '').localeCompare(b.daySegment?.start ?? ''));

    const autoClockOut = data.provider?.schedules.some((item) => item.clockedOut === false);

    // The payee has split checks if they own one (a split override on their own segments)
    // or receive a share from any split check across the POS data.
    const ownsSplitCheck = data.segments.some((segment) => segment.tickets.some((ticket) => ticket.override?.type === TicketOverrideType.SPLIT));
    const receivesSplitShare = [data.employee?.id, data.provider.employeeId].some((id) => id && splitCheckShareParticipantIds.has(id));

    payees.push({
      id: employeeId,
      name: data.employee?.name || data.provider.name,
      labor: laborData,
      provider: data.provider,
      hasSplitChecks: ownsSplitCheck || receivesSplitShare,
      dataIndex: idx,
      autoClockOut,
    });
  });

  // sort by employee first name or provider first name
  return payees.sort(sortAlphaNumerically('name'));
};

export interface ITipCalculatorPoolTotals {
  retainedHouse: number | undefined;
  retainedTotal: number;
  pooledTotal: number;
}

/**
 * Calculates the totals for the pool data.
 *
 * @param {ITipCalculatorResult[]} tipCalcResults - The tip calculation results to use.
 * @returns {ITipCalculatorPoolTotals} The totals for the pool data.
 */
export const calculateTipCalculatorPoolTotals = (tipCalcResults: ITipCalculatorResult[]): ITipCalculatorPoolTotals => {
  const sumReceived = (received: Array<{ total: number }>) => Math.trunc(received.reduce((sum, item) => sum + item.total, 0));

  const totals: ITipCalculatorPoolTotals = tipCalcResults.reduce<ITipCalculatorPoolTotals>(
    (acc, result) => {
      getPoolMemberTotals(result).forEach(({ total }) => {
        acc.pooledTotal += total;
      });

      result.pools.forEach((pool) => {
        acc.retainedTotal += pool.retained.total;
      });

      result.participants?.forEach((participant) => {
        acc.retainedHouse += sumReceived(participant.received);
      });

      return acc;
    },
    { retainedHouse: 0, retainedTotal: 0, pooledTotal: 0 }
  );

  return totals;
};

/**
 * Splits a list of received entries into their tips and autograts totals.
 *
 * @param {ITipCalculatorResultReceived[]} received - The received entries to sum.
 * @returns {{ tips: number; autograts: number }} The summed tips and autograts amounts.
 */
export const getReceivedTipTotals = (received: ITipCalculatorResultReceived[] = []): { tips: number; autograts: number } =>
  received.reduce(
    (acc, entry) => {
      entry.sources.forEach(({ source, total }) => {
        if (source === RuleFundSource.TIPS) acc.tips += total || 0;
        else if (source === RuleFundSource.AUTO_GRATUITIES) acc.autograts += total || 0;
      });
      return acc;
    },
    { tips: 0, autograts: 0 }
  );

/**
 * Selects a segment's tip-pool members — excluding owner-rule employees, optionally narrowed to one job title — and
 * pairs each with its received tips/autograts split.
 *
 * @param {ITipCalculatorResult} result - The tip calculation result for a single segment.
 * @param {string} [jobTitle] - Restricts to this job title (case/whitespace-insensitive); omit to include all jobs.
 * @returns {IPoolEmployeeRow[]} Members with their tips, autograts, and combined total (percentWorked added later by the report).
 */
export const getPoolMemberTotals = (result: ITipCalculatorResult, jobTitle?: string): IPoolEmployeeRow[] => {
  const ownerProviderIds = getOwnerRuleProviderIds(result.rules);
  const normalizedJobTitle = jobTitle ? normalizeJobTitle(jobTitle) : undefined;

  return result.employees.reduce<IPoolEmployeeRow[]>((rows, employee) => {
    if (!ownerProviderIds.has(employee.provider?.id) && (!normalizedJobTitle || normalizeJobTitle(employee.job.title) === normalizedJobTitle)) {
      const { tips, autograts } = getReceivedTipTotals(employee.received);
      rows.push({ employee, tips, autograts, total: tips + autograts });
    }
    return rows;
  }, []);
};

/**
 * Aggregates what a job's pool received into one row per contributor and distribution.
 *
 * Each pool member's receipts are grouped by contributor and distribution (share vs remainder) and their amounts summed.
 * Every row is then labeled from the rule mechanism its distribution came through — a share carries its percentage and
 * sources, a remainder pool carries its sources. Splitting on distribution keeps a contributor's share and remainder as
 * distinct, correctly-labeled rows; a receipt whose contributor has no matching rule is dropped.
 *
 * @param {IPoolEmployeeRow[]} poolMembers - The pool's members (from getPoolMemberTotals).
 * @param {ICalculatorRule[]} rules - The segment's calculator rules.
 * @param {string} job - The job title of the pool.
 * @returns {IPoolReceivedFrom[]} The received-from rows, in first-seen order.
 */
export const buildPoolReceivedFrom = (poolMembers: IPoolEmployeeRow[], rules: ICalculatorRule[], job: string): IPoolReceivedFrom[] => {
  const normalizedJob = normalizeJobTitle(job);

  // Step 1: total each contributor's received across members, keyed by distribution (a share and a remainder from same contributor stay separate), keeping one entry per key to describe below.
  const totalsByKey = new Map<string, { entry: ITipCalculatorResultReceived; total: number }>();
  poolMembers.forEach(({ employee }) => {
    employee.received?.forEach((received) => {
      const key = `${normalizeJobTitle(received.from.name)}|${received.distribution}`;
      const existing = totalsByKey.get(key);
      if (existing) {
        existing.total += received.total;
      } else {
        totalsByKey.set(key, { entry: received, total: received.total });
      }
    });
  });

  // Step 2: describe each row from the rule mechanism matching its distribution — a share or the remainder pool; skip rows no rule pays.
  const rows: IPoolReceivedFrom[] = [];
  totalsByKey.forEach(({ entry, total }) => {
    const contributor = normalizeJobTitle(entry.from.name);
    const rule = rules.find((r) => normalizeJobTitle(r.contributor?.name || '') === contributor || r.additionalContributors?.some((additional) => normalizeJobTitle(additional.name) === contributor));
    if (!rule) return;

    const isShare = entry.distribution === TipCalculatorReceivedDistributionType.SHARE;
    const isRemainder = entry.distribution === TipCalculatorReceivedDistributionType.REMAINDER;
    const jobShare = isShare ? rule.sharePolicy?.shares?.find((share) => share.beneficiaries?.some((beneficiary) => normalizeJobTitle(beneficiary.name) === normalizedJob)) : undefined;
    const remainder = isRemainder ? rule.sharePolicy?.remainder?.beneficiaries?.find((beneficiary) => beneficiary.participants.some((participant) => normalizeJobTitle(participant.name) === normalizedJob)) : undefined;
    if (!jobShare && !remainder) return; // this rule doesn't pay this job through the entry's distribution

    rows.push({
      name: contributor,
      total,
      distribution: entry.distribution,
      conditions: entry.conditions,
      category: entry.category,
      split: entry.split,
      eligibilityMethod: entry.eligibilityMethod,
      sources: getTipSources(jobShare?.sources || remainder?.sources || []),
      sharePercent: jobShare?.share ? toPercent(jobShare.share) : undefined,
    });
  });

  return rows;
};

/**
 * Builds every value the pool report renders for a given job title from a tip calculation result.
 *
 * Totals are derived from the received arrays of the employees that benefit from the pool (owner-rule employees excluded),
 * summing each employee's actual payout so amounts are exact and require no truncation.
 *
 * @param {ITipCalculatorResult} result - The tip calculation result for the segment.
 * @param {string} jobTitle - The job title of the pool being displayed.
 * @returns {IPoolReport} The employee rows, received-from rows, and aggregated totals for the pool.
 */
export const buildPoolReport = (result: ITipCalculatorResult, jobTitle: string): IPoolReport => {
  const baseRows = getPoolMemberTotals(result, jobTitle);

  // Sum the pool totals from the member rows.
  const { totalReceivedTips, totalReceivedAutograts, totalMinWorked } = baseRows.reduce(
    (acc, row) => {
      acc.totalReceivedTips += row.tips;
      acc.totalReceivedAutograts += row.autograts;
      acc.totalMinWorked += row.employee.totals.totalMinWorked || 0;
      return acc;
    },
    { totalReceivedTips: 0, totalReceivedAutograts: 0, totalMinWorked: 0 }
  );
  const totalReceived = totalReceivedTips + totalReceivedAutograts;

  // Attach each row's percent share of the pool (needs the pool total above).
  const employeeRows: IPoolEmployeeRow[] = baseRows.map((row) => ({ ...row, percentWorked: totalReceived === 0 ? '0%' : toPercent(row.total / totalReceived, 1) }));

  const receivedFromRows = buildPoolReceivedFrom(employeeRows, result.rules, jobTitle);

  return { employeeRows, receivedFromRows, totalMinWorked, totalReceivedTips, totalReceivedAutograts, totalReceived };
};

/**
 * Summarizes a segment's pools for the pool list: one row per job (its members' minutes and received tips + autograts)
 * plus the segment totals. Derived from members' actual payouts instead of pools, so it needs no truncation.
 * Jobs whose members were paid nothing this segment are omitted entirely and do not contribute its time here.
 *
 * @param {ITipCalculatorResult} result - The tip calculation result for a single segment.
 * @returns {IPoolData} Per-job rows (jobs that received nothing are omitted), segment totals, and the underlying result.
 */
export const buildPoolSummary = (result: ITipCalculatorResult): IPoolData => {
  // Group the pool members by job, summing each job's minutes and received tips + autograts.
  const jobs = new Map<string, IPoolJob>();
  getPoolMemberTotals(result).forEach(({ employee, total }) => {
    const normalizedJobTitle = normalizeJobTitle(employee.job.title);
    const existing = jobs.get(normalizedJobTitle);

    if (existing) {
      existing.totalMinutes += employee.totals.totalMinWorked || 0;
      existing.totalTips += total;
    } else {
      jobs.set(normalizedJobTitle, { jobTitle: employee.job.title, totalMinutes: employee.totals.totalMinWorked || 0, totalTips: total });
    }
  });

  // Keep only jobs that received something, summing the segment totals.
  const pools: IPoolJob[] = [];
  let totalMinutes = 0;
  let totalTips = 0;

  jobs.forEach((job) => {
    if (job.totalTips > 0) {
      pools.push(job);
      totalMinutes += job.totalMinutes;
      totalTips += job.totalTips;
    }
  });

  return { daySegment: result.segment, totalMinutes, totalTips, pools, result };
};

export interface ITipCalculatorTipDataTotals {
  collectedTipsAndAutograts: number;
  creditCardTips: number;
  cashTips: number;
  creditCardAutograts: number;
  cashAutograts: number;
  creditCardFee: number;
  splitChecksTotal: number;
  ignoredChecksTotal: number;
  calculatedNet: number;
}

/**
 * Calculates the totals for the tip data.
 *
 * @param {IPOSResults} posResults - The POS results to use.
 * @returns {ITipCalculatorTipDataTotals} The totals for the tip data.
 */
export const calculateTipCalculatorTipDataTotals = (posResults: IPOSResults): ITipCalculatorTipDataTotals => {
  const tipTotals = (posResults?.data ?? []).reduce(
    (acc, entry) => {
      entry.segments.forEach((seg) => {
        acc.cashTips += seg.totals.tip.cash;
        acc.cashAutograts += seg.totals.autoGratuity.cash;
        acc.creditCardTips += seg.totals.tip.creditCard;
        acc.creditCardAutograts += seg.totals.autoGratuity.creditCard;
        acc.creditCardFee += seg.totals.tip.creditCardFee + seg.totals.autoGratuity.creditCardFee;
      });

      return acc;
    },
    { cashTips: 0, cashAutograts: 0, creditCardTips: 0, creditCardAutograts: 0, creditCardFee: 0 }
  );

  const checkOverrideTotals = buildSortedChecksList(posResults).reduce(
    (acc, check) => {
      const checkNetTotal = check.totals.tip.net + check.totals.autoGratuity.net;
      const checkCreditCardFeeTotal = check.totals.tip.creditCardFee + check.totals.autoGratuity.creditCardFee;

      if (check.override?.type === TicketOverrideType.SPLIT) {
        acc.splitChecksTotal += checkNetTotal;
        acc.creditCardFeeFromOverrides += checkCreditCardFeeTotal;
      }

      if (check.override?.type === TicketOverrideType.IGNORE) {
        acc.ignoredChecksTotal += checkNetTotal;
        acc.creditCardFeeFromOverrides += checkCreditCardFeeTotal;
      }

      return acc;
    },
    { splitChecksTotal: 0, ignoredChecksTotal: 0, creditCardFeeFromOverrides: 0 }
  );

  const creditCardFee = tipTotals.creditCardFee + checkOverrideTotals.creditCardFeeFromOverrides;

  // calculated net is the net after deductions
  const calculatedNet = tipTotals.cashTips + tipTotals.cashAutograts + tipTotals.creditCardTips + tipTotals.creditCardAutograts - checkOverrideTotals.splitChecksTotal - checkOverrideTotals.ignoredChecksTotal - creditCardFee;

  return {
    collectedTipsAndAutograts: tipTotals.cashTips + tipTotals.cashAutograts + tipTotals.creditCardTips + tipTotals.creditCardAutograts,
    creditCardTips: tipTotals.creditCardTips,
    cashTips: tipTotals.cashTips,
    creditCardAutograts: tipTotals.creditCardAutograts,
    cashAutograts: tipTotals.cashAutograts,
    creditCardFee,
    splitChecksTotal: checkOverrideTotals.splitChecksTotal,
    ignoredChecksTotal: checkOverrideTotals.ignoredChecksTotal,
    calculatedNet,
  };
};

interface IDaySegmentSummary extends IDaySegment {
  totalJobMinutes: number;
}

export interface ITipCalculatorLaborDataTotals {
  totalTippedMinutes: number;
  segments: IDaySegmentSummary[];
}

/**
 * Calculates the totals for the labor data.
 *
 * Mirrors the labor list visibility rules in LaborData.tsx: include any row that has real labor
 * data (a schedule and matching contributor rule) and is not a virtual rule contributor (shared POS
 * terminals — those represent aggregated terminal time, not a single employee's labor).
 *
 * @param {IPOSResults} posResults - The POS results to use.
 * @param {IDaySegment[]} paymentSetSegments - The payment set segments to use.
 * @param {ICalculatorRules} calculatorRules - The calculator rules to use.
 * @returns {ITipCalculatorLaborDataTotals} The totals for the labor data.
 */
export const calculateTipCalculatorLaborDataTotals = (posResults: IPOSResults, paymentSetSegments: IDaySegment[], calculatorRules: ICalculatorRules): ITipCalculatorLaborDataTotals => {
  const segmentTotals: IDaySegmentSummary[] = [];

  // initialize each segment to 0
  paymentSetSegments.forEach((segment) => {
    segmentTotals.push({ ...segment, totalJobMinutes: 0 });
  });

  // calculate total minutes for each segment
  posResults.data.forEach((data) => {
    if (!data.segments?.length || !data.provider?.schedules?.length) return;

    if (isVirtualRuleContributor(data, calculatorRules)) return;

    data.segments.forEach((seg) => {
      const match = segmentTotals.find((item) => item.id === seg.id);
      const segmentJobTitle = seg.job.title.toLowerCase().trim();
      if (match && seg.totals.totalMinWorked > 0 && calculatorRules.rules.some((rule) => rule.contributor && rule.contributor.name.toLowerCase().trim() === segmentJobTitle)) {
        match.totalJobMinutes += seg.totals.totalMinWorked;
      }
    });
  });

  return {
    totalTippedMinutes: segmentTotals.reduce((acc, item) => acc + item.totalJobMinutes, 0),
    segments: segmentTotals,
  };
};

// Aggregates payment totals across tip-calculator payees.
//
// Totals returned:
// - gross: sum of each payee's gross tip allocation
// - cash: sum of cash tips paid out directly (not disbursed through Kickfin)
// - holdbacks: sum of holdback amounts retained from instant disbursements
// - instant: sum of instant disbursement amounts (gross - withholding - cash) for INSTANT payees
// - payroll: sum of amounts to be handled via payroll:
//   - PAYROLL payees: (gross - cash)
//   - INSTANT payees: withholding amount
// - payNow: amount funded/charged now via Kickfin (instant - holdbacks)
export const calculateTipCalculatorPaymentTotals = (payees: ITipCalculatorPayee[]): IPaymentTotals =>
  (payees || []).reduce(
    (acc, payee) => {
      const instant = calculateInstant({ ...payee, holdbackAmount: 0 });
      acc.holdbacks += payee.holdbackAmount;
      acc.instant += instant;
      acc.payroll += calculatePayroll(payee);
      acc.cash += payee.cash;
      acc.gross += payee.gross;
      acc.payNow += instant - payee.holdbackAmount;
      return acc;
    },
    { gross: 0, payNow: 0, cash: 0, instant: 0, payroll: 0, holdbacks: 0 }
  );

type IAdjustmentParticipant = ITipCalculatorPayee | ITipCalculatorParticipant;

/**
 * Names for an adjustment row.
 *
 * A payee with no linked Kickfin employee — a POS record that has never been matched — carries only the
 * provider's name, which can be blank. Falling back to the POS employee id keeps the row identifiable
 * instead of rendering an anonymous amount, and the empty-string guard keeps `split` off `undefined`.
 *
 * The id fallback applies to payees only: participants (house accounts, job pools) carry no `provider`, and
 * always have a name. If participants ever become nameless they will need their own fallback.
 */
const getAdjustmentItem = (item: IAdjustmentParticipant) => {
  let firstName;
  let lastName;
  let employeeId;
  if ('employee' in item && item.employee) {
    firstName = item.employee.firstName;
    lastName = item.employee.lastName;
    employeeId = item.employee.employeeId;
  } else {
    [firstName, lastName] = (item.name || '').split(' ', 2);
    if (!firstName) firstName = ('provider' in item && item.provider?.employeeId) || '';
  }
  return { firstName, lastName: lastName || '', employeeId, gross: item.gross, autoGratuity: item.autoGratuity };
};

const calculateAdjustments = (original: IAdjustmentParticipant[], current: IAdjustmentParticipant[]): IPaymentAdjustment[] => {
  const originalMap = new Map(original.map((p) => [(p as ITipCalculatorPayee).id || p.name, getAdjustmentItem(p)]));
  const currentMap = new Map(current.map((p) => [(p as ITipCalculatorPayee).id || p.name, getAdjustmentItem(p)]));
  const allKeys = Array.from(new Set([...originalMap.keys(), ...currentMap.keys()]));

  return allKeys.reduce((acc, key) => {
    const origItem = originalMap.get(key);
    const currItem = currentMap.get(key);

    const originalGross = origItem?.gross || 0;
    const currentGross = currItem?.gross || 0;
    const difference = currentGross - originalGross;

    const originalAutograts = origItem?.autoGratuity || 0;
    const currentAutograts = currItem?.autoGratuity || 0;
    const autogratsDifference = currentAutograts - originalAutograts;
    const tipsDifference = difference - autogratsDifference;

    if (tipsDifference !== 0 || autogratsDifference !== 0) {
      acc.push({
        // Either side can be absent: a payee who only appears in the recalculation has no original, and one
        // dropped from it has no current. Dereferencing the missing side crashed the modal for new unlinked
        // employees, whose names are blank (KICK-5129).
        firstName: currItem?.firstName || origItem?.firstName || '',
        lastName: currItem?.lastName || origItem?.lastName || '',
        // No fallback to the original side: it is rebuilt from payouts, which carry the Kickfin employee id
        // rather than the customer-assigned one, so a payee dropped from the recalculation has none to show.
        // The names above do fall through, and keep `||` because an empty name should defer to the other side.
        employeeId: currItem?.employeeId,
        original: originalGross,
        recalculated: currentGross,
        difference,
        tips: tipsDifference,
        autograts: autogratsDifference,
      });
    }
    return acc;
  }, []);
};

/**
 * Calculates the tips & autograts differences between a payment set and the current set of payees and participants.
 *
 * @param {IPaymentSet} originalPayment - The previously submitted payment set used as the baseline for comparison.
 * @param {ITipCalculatorPayee[]} payees - The current list of payees in the tip calculator.
 * @param {ITipCalculatorParticipant[]} participants - The current list of participants in the tip calculator.
 * @returns {IPaymentAdjustment[]} An array containing the calculated adjustments for both participants and payees.
 */
export const getPaymentAdjustments = (originalPayment: IPaymentSet, payees: ITipCalculatorPayee[], participants: ITipCalculatorParticipant[]): IPaymentAdjustment[] => {
  const originalParticipants = buildTipCalculatorParticipants(originalPayment.calculations.results);
  const originalPayees = buildGroupedTipCalculatorPayeesFromPayouts(originalPayment);
  const participantAdjustments = calculateAdjustments(originalParticipants, participants);
  const payeeAdjustments = calculateAdjustments(originalPayees, payees);

  return [...participantAdjustments, ...payeeAdjustments];
};

/**
 * Returns a friendly display string for the contributor type of a calculator rule.
 *
 * @param {ICalculatorRule} tipShare - The calculator rule containing the contributor information.
 * @returns {string} A label representing the contributor type ('Job', 'Owner', or 'Contributor' as default case).
 */
export const displayContributorType = (tipShare: ICalculatorRule) => {
  switch (tipShare.contributor.type) {
    case CalculatorParticipantType.JOB:
      return 'Job';
    case CalculatorParticipantType.EMPLOYEE:
      return 'Owner';
    default:
      return 'Contributor';
  }
};
